/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import axios from "axios";
import toast from "react-hot-toast";
import { API_URL } from "../config";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { getLookupPiyasalar, getLookupParaBirimleri, getLookupPeriyotlar } from "./apiCache";


// ─── Sabitler ────────────────────────────────────────────────────────────────

const VADE_OPTS = {
    BIST:   ["Gün İçi", "Kısa Vade (1-10 gün)", "Orta Vade (11-60 gün)", "Uzun Vade (61+ gün)"],
    Kripto: ["Gün İçi", "Kısa Vade (1-10 gün)", "Orta Vade (11-60 gün)", "Uzun Vade (61+ gün)", "Kaldıraçlı İşlem"],
};

const MIN_SKOR_OPTS = [0, 50, 60, 65, 70, 75, 80];

const ZAMAN_DİLİMLERİ = {
    "Uzun Vade (61+ gün)":   { "Ana Trend": "Aylık", "Yapı": "Haftalık", "Giriş": "Günlük" },
    "Orta Vade (11-60 gün)": { "Ana Trend": "Haftalık", "İşlem Yönü": "Günlük", "Giriş": "4 Saatlik" },
    "Kısa Vade (1-10 gün)":  { "Ana Trend": "Günlük", "İşlem Yönü": "4 Saatlik", "Giriş": "1 Saatlik" },
    "Gün İçi":               { "Trend": "1 Saatlik", "Giriş": "15 Dakika", "Hassas Giriş": "5 Dakika" },
    "Kaldıraçlı İşlem":      { "Trend": "4 Saatlik", "Yapı": "1 Saatlik", "Giriş": "15 Dakika" },
};

// ─── Renk yardımcıları ───────────────────────────────────────────────────────

const sinifRenk = (sinif) => {
    if (!sinif) return { bg: "#334155", color: "#94a3b8" };
    if (sinif.startsWith("A+")) return { bg: "#16a34a22", color: "#15803d" };
    if (sinif.startsWith("A"))  return { bg: "#05966922", color: "#059669" };
    if (sinif.startsWith("B"))  return { bg: "#2563eb22", color: "#2563eb" };
    if (sinif.startsWith("C"))  return { bg: "#f59e0b22", color: "#d97706" };
    return { bg: "#334155", color: "#94a3b8" };
};

const kararRenk = (karar) => {
    if (!karar) return "#94a3b8";
    if (karar.includes("GÜÇLÜ SAT")) return "#7f1d1d";
    if (karar.includes("SAT"))       return "#dc2626";
    if (karar.includes("GÜÇLÜ AL"))  return "#15803d";
    if (karar.includes("AL"))        return "#2563eb";
    if (karar.includes("İZLE"))      return "#d97706";
    return "#94a3b8";
};

const skorRenk = (s, yon) => {
    if (yon === "short") {
        // Short'ta yüksek skor = güçlü sat = kırmızı
        return s >= 80 ? "#7f1d1d" : s >= 65 ? "#dc2626" : s >= 50 ? "#d97706" : "#94a3b8";
    }
    return s >= 80 ? "#15803d" : s >= 65 ? "#2563eb" : s >= 50 ? "#d97706" : "#dc2626";
};
const skorBg = (s, yon) => {
    if (yon === "short") {
        return s >= 80 ? "#dc262633" : s >= 65 ? "#dc262622" : s >= 50 ? "#f59e0b22" : "#334155";
    }
    return s >= 80 ? "#16a34a22" : s >= 65 ? "#2563eb22" : s >= 50 ? "#f59e0b22" : "#dc262622";
};
const rejimRenk = (r) => !r ? "#94a3b8" : r.includes("Güçlü Boğa") ? "#15803d" : r.includes("Boğa") ? "#059669" : r.includes("Yatay") ? "#d97706" : "#dc2626";

const yuzde = (fiyat, hedef) => {
    if (!fiyat || !hedef || fiyat <= 0) return null;
    return ((hedef - fiyat) / fiyat * 100).toFixed(1);
};

// ─── Skor Çubuğu ─────────────────────────────────────────────────────────────

const COKLU_MODULLER = [
    { label: "📊 Dip Tarama",           value: "Dip Tarama",           pasif: false },
    { label: "🌊 Likidite Tarama",       value: "Likidite Tarama",      pasif: false },
    { label: "📈 FVG Tarama",            value: "FVG Tarama",           pasif: false },
    { label: "⚓ Çapa Noktası Tarama",   value: "Çapa Noktası Tarama",  pasif: false },
    { label: "📐 MSB Tarama",            value: "MSB Tarama",           pasif: false },
    { label: "🎯 OTE Tarama",            value: "OTE Tarama",           pasif: false },
    { label: "🧱 OB/BB Tarama",          value: "OB & BB Tarama",       pasif: false },
    { label: "🎣 IDM Tarama",            value: "IDM Tarama",           pasif: false },
    { label: "⚖️ P/D Zone Tarama",       value: "P/D Zone Tarama",      pasif: false },
    { label: "🔀 SMT Tarama",            value: "SMT Tarama",           pasif: false },
    { label: "⏰ Killzone Tarama",        value: "Killzone Tarama",      pasif: false },
    { label: "🌿 Topdown Cascade",        value: "Topdown Cascade",      pasif: false },
    { label: "📊 Hacim Profili Tarama",   value: "Hacim Profili Tarama", pasif: false },
    { label: "🌊 Hacim Akışı Tarama",     value: "Hacim Akışı Tarama",   pasif: false },
    { label: "📉 Divergence Tarama",      value: "Divergence Tarama",    pasif: false },
];

const _PERIYOT_OPTS_CK = ["1 Gün", "1 Hafta", "1 Ay", "3 Aylık", "6 Aylık", "Yıllık"];
const _KZ_LISTESI_CK   = [
    "Asya KZ (03:00-09:00)", "Londra Açılış (10:00-12:00)", "New York Açılış (15:00-17:00)",
    "Londra Kapanış (18:00-20:00)", "BIST Açılış (10:00-11:00)",
    "BIST Öğle Dönüşü (13:00-14:00)", "BIST Kapanış Öncesi (17:00-18:00)",
];

const MODUL_KEY_MAP = {"Dip Tarama":"dip_tarama","Likidite Tarama":"likidite_tarama","FVG Tarama":"fvg_tarama","Çapa Noktası Tarama":"capa_noktasi_tarama","MSB Tarama":"msb_tarama","OTE Tarama":"ote_tarama","OB & BB Tarama":"obbb_tarama","IDM Tarama":"idm_tarama","P/D Zone Tarama":"pd_zone_tarama","SMT Tarama":"smt_tarama","Killzone Tarama":"killzone_tarama","Topdown Cascade":"topdown_cascade_tarama","Hacim Profili Tarama":"hacim_profili_tarama","Hacim Akışı Tarama":"hacim_akisi_tarama","Divergence Tarama":"divergence_tarama"};

function defaultCokluFiltreler(modul, piyasa) {
    switch (modul) {
        case "Dip Tarama":       return { tarama_periyot: "Aylık", tolerans: 1 };
        case "Likidite Tarama":  return { sweep_tipler: { "Boğa Sweep": true, "Ayı Sweep": false }, tarama_periyot: "1 Gün", lookback: 20, wick_oran: 50 };
        case "FVG Tarama":       return { fvg_tipler: { "Boğa FVG": true, "Ayı FVG": false, "iFVG": false }, bpr_kontrol: "Hayır", tarama_periyot: "1 Gün", mum_sayisi: 100 };
        case "Çapa Noktası Tarama": return { fvg_tipler: { "Boğa FVG": true, "Ayı FVG": false }, tolerans: 30 };
        case "MSB Tarama":       return { msb_tipler: { "Boğa MSB": true, "Ayı MSB": false }, tarama_periyot: "1 Ay", swing_pencere: 3 };
        case "OTE Tarama":       return { ote_tipler: { "Boğa OTE": true, "Ayı OTE": false }, tarama_periyot: "1 Hafta", swing_pencere: 3, mum_sayisi: 100 };
        case "OB & BB Tarama":   return { obbb_tipler: { "Boğa OB": true, "Ayı OB": false, "Boğa BB": false, "Ayı BB": false }, tarama_periyot: "1 Gün", mum_sayisi: 200, govde_oran: 60, sadece_taze: true };
        case "IDM Tarama":       return { idm_tipler: { "Boğa IDM": true, "Ayı IDM": false }, tarama_periyot: "1 Gün", mum_sayisi: 200 };
        case "P/D Zone Tarama":  return { bolge_filtre: { "Discount": true, "Equilibrium": false, "Premium": false }, tarama_periyot: "1 Gün", range_window: 20, mum_sayisi: 200 };
        case "SMT Tarama": return { smt_tipler: { "Boğa SMT": true, "Ayı SMT": false }, tarama_periyot: "1 Gün", referans_varlik: piyasa === "BIST" ? "XU100 (BIST100)" : "BTC", swing_pencere: 3, kor_pencere: 50, min_korelasyon: 70, mum_sayisi: 200 };
        case "Killzone Tarama":  return { killzoneler: { "BIST Açılış (10:00-11:00)": true }, yapi_tipleri: { "FVG": true, "MSB": false, "Sweep": false }, geri_bak_gun: 5 };
        case "Topdown Cascade":   return { topdown_mod: piyasa };
        case "Hacim Profili Tarama": return { tarama_periyot: "1 Gün", sinyal_filtre: [], min_skor: 0, mum_sayisi: 300 };
        case "Hacim Akışı Tarama": return { lookback: 20, min_spike: 1.5, sinyal_filtre: {"📈 Güçlü Alım":true,"🔶 Orta Alım":true,"⬇️ Zayıf Alım":false,"📉 Güçlü Satım":true,"🔶 Orta Satım":true,"⬆️ Zayıf Satım":false,"⚖️ Belirsiz":false} };
        case "Divergence Tarama": return { tarama_periyot: "1 Gün", pivot_modu: "fractal", osilatorler: {"RSI":true,"MACD Histogram":true,"Stokastik RSI":true,"OBV":false}, divergence_tipleri: {"Regular Bullish":true,"Regular Bearish":true,"Hidden Bullish":false,"Hidden Bearish":false}, swing_pencere:5, zigzag_esik:1, n_son_pivot:2, min_pivot_mesafe:3, min_hareket_atr:0, rsi_periyot:14, macd_fast:12, macd_slow:26, macd_signal:9, stoch_rsi_periyot:14, stoch_periyot:14, stoch_k:3, stoch_d:3, ayarlar_acik:false };
        default: return {};
    }
}

function CokluTaramaGonderPopup({ piyasa, paraBirimi, secilenSayisi, secilenModul, filtre, periyotlar, onModulSec, onFiltreDegis, onIptal, onBaslat }) {
    const periyotOpts = periyotlar && periyotlar.length > 0 ? periyotlar : _PERIYOT_OPTS_CK;
    const set = (key, val) => onFiltreDegis({ ...filtre, [key]: val });
    const ck  = (grup, key) => onFiltreDegis({ ...filtre, [grup]: { ...filtre[grup], [key]: !filtre[grup][key] } });

    const CheckGrup = ({ alan, etiket }) => (
        <div style={{ marginBottom: "10px" }}>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#93c5fd", marginBottom: "6px" }}>{etiket}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {Object.keys(filtre[alan] || {}).map(k => (
                    <label key={k} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", cursor: "pointer" }}>
                        <input type="checkbox" checked={!!filtre[alan][k]} onChange={() => ck(alan, k)} />{k}
                    </label>
                ))}
            </div>
        </div>
    );

    const Sel = ({ alan, etiket, opts, labelFn }) => (
        <div style={{ marginBottom: "10px" }}>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#93c5fd", marginBottom: "6px" }}>{etiket}</div>
            <select value={filtre[alan]} onChange={e => set(alan, isNaN(e.target.value) ? e.target.value : Number(e.target.value))}
                style={{ padding: "6px 10px", borderRadius: "7px", border: "1px solid #334155", background: "#0f172a", color: "#e2e8f0", fontSize: "13px", width: "100%" }}>
                {opts.map(o => <option key={o} value={o}>{labelFn ? labelFn(o) : o}</option>)}
            </select>
        </div>
    );

    const renderFiltreler = () => {
        if (!filtre) return null;
        switch (secilenModul) {
            case "Dip Tarama":       return (<><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="tolerans" etiket="Tolerans" opts={[1,3,5]} labelFn={v=>`%${v}`} /></>);
            case "Likidite Tarama":  return (<><CheckGrup alan="sweep_tipler" etiket="Sweep Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="lookback" etiket="Lookback" opts={[10,20,50,100]} labelFn={v=>`${v} Mum`} /><Sel alan="wick_oran" etiket="Min Fitil" opts={[30,40,50,60,70]} labelFn={v=>`%${v}`} /></>);
            case "FVG Tarama":       return (<><CheckGrup alan="fvg_tipler" etiket="FVG Tipi" /><Sel alan="bpr_kontrol" etiket="BPR Kontrol" opts={["Evet","Hayır"]} /><Sel alan="tarama_periyot" etiket="Tarama Periyodu" opts={periyotOpts} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[25,50,100,150,200,250,300,350,400,500,600,700]} /></>);
            case "Çapa Noktası Tarama": return (<><CheckGrup alan="fvg_tipler" etiket="FVG Tipi" /><Sel alan="tolerans" etiket="Tolerans" opts={[30,50,70,80,90,100]} labelFn={v=>`%${v}`} /></>);
            case "MSB Tarama":       return (<><CheckGrup alan="msb_tipler" etiket="MSB Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="swing_pencere" etiket="Swing Penceresi" opts={[2,3,4,5,6,7,8]} labelFn={v=>`${v} Mum`} /></>);
            case "OTE Tarama":       return (<><CheckGrup alan="ote_tipler" etiket="OTE Tipi" /><Sel alan="tarama_periyot" etiket="Tarama Periyodu" opts={periyotOpts} /><Sel alan="swing_pencere" etiket="Swing Penceresi" opts={[2,3,4,5,6,7,8]} labelFn={v=>`${v} Mum`} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[50,100,150,200,300]} /></>);
            case "OB & BB Tarama":   return (<><CheckGrup alan="obbb_tipler" etiket="OB/BB Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300,500]} /><Sel alan="govde_oran" etiket="Min Gövde" opts={[40,50,60,70]} labelFn={v=>`%${v}`} /><div style={{marginBottom:"10px"}}><label style={{display:"flex",alignItems:"center",gap:"6px",fontSize:"12px",cursor:"pointer"}}><input type="checkbox" checked={filtre.sadece_taze} onChange={e=>set("sadece_taze",e.target.checked)} />Sadece Taze</label></div></>);
            case "IDM Tarama":       return (<><CheckGrup alan="idm_tipler" etiket="IDM Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300,500]} /></>);
            case "P/D Zone Tarama":  return (<><CheckGrup alan="bolge_filtre" etiket="Bölge" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="range_window" etiket="Range Penceresi" opts={[10,20,50,100]} labelFn={v=>`${v} Mum`} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300,500]} /></>);
            case "SMT Tarama": return (<><CheckGrup alan="smt_tipler" etiket="SMT Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Referans Varlık</div><select value={filtre.referans_varlik} onChange={e=>set("referans_varlik",e.target.value)} style={{padding:"6px 10px",borderRadius:"7px",border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:"13px",width:"100%"}}>{(piyasa==="BIST"?["XU100 (BIST100)","XU030 (BIST30)"]:["BTC","ETH","BNB"]).map(v=><option key={v}>{v}</option>)}</select></div><Sel alan="swing_pencere" etiket="Swing Penceresi" opts={[2,3,5]} labelFn={v=>`${v} Mum`} /><Sel alan="min_korelasyon" etiket="Min Korelasyon" opts={[50,60,70,80,90]} labelFn={v=>`%${v}`} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300]} /></>);
            case "Killzone Tarama":  return (<><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Killzone</div><div style={{display:"flex",flexWrap:"wrap",gap:"8px"}}>{_KZ_LISTESI_CK.filter(k=>piyasa==="BIST"||!k.includes("BIST")).map(k=><label key={k} style={{display:"flex",alignItems:"center",gap:"4px",fontSize:"12px",cursor:"pointer"}}><input type="checkbox" checked={!!filtre.killzoneler[k]} onChange={()=>ck("killzoneler",k)} />{k.split("(")[0].trim()}</label>)}</div></div><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Yapı Tipi</div><div style={{display:"flex",gap:"12px"}}>{["FVG","MSB","Sweep"].map(k=><label key={k} style={{display:"flex",alignItems:"center",gap:"4px",fontSize:"12px",cursor:"pointer"}}><input type="checkbox" checked={!!filtre.yapi_tipleri[k]} onChange={()=>ck("yapi_tipleri",k)} />{k}</label>)}</div></div><Sel alan="geri_bak_gun" etiket="Geriye Bakış" opts={[1,3,5,10]} labelFn={v=>`Son ${v} Gün`} /></>);
            case "Topdown Cascade": return (<div style={{fontSize:"13px",color:"#94a3b8",padding:"8px 0"}}>{piyasa} — EMA200 + 4H Sweep taraması. Ek kriter seçimi gerekmez.</div>);
            case "Hacim Profili Tarama": {
                const HP_SF = ["POC Yakını","Discount","Premium","HVN Yakın","LVN İçi"];
                const hpSF = filtre.sinyal_filtre || [];
                const toggleHpSF = v => { const sf = Array.isArray(hpSF)?hpSF:[]; set("sinyal_filtre", sf.includes(v)?sf.filter(x=>x!==v):[...sf,v]); };
                return (<>
                    <Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} />
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Sinyal Filtresi</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:"6px"}}>{HP_SF.map(v=>{const aktif=Array.isArray(hpSF)&&hpSF.includes(v);return(<button key={v} type="button" onClick={()=>toggleHpSF(v)} style={{padding:"3px 10px",borderRadius:20,fontSize:11,cursor:"pointer",fontWeight:aktif?600:400,border:`1px solid ${aktif?"#3b82f6":"#334155"}`,background:aktif?"#3b82f622":"transparent",color:aktif?"#3b82f6":"#94a3b8"}}>{v}</button>);})}</div>
                    </div>
                    <Sel alan="min_skor" etiket="Min Skor" opts={[0,2,3,4,5,6,7]} labelFn={v=>v===0?"Tümü":`≥${v}`} />
                    <Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300,500]} />
                </>);
            }
            case "Hacim Akışı Tarama": {
                const HA_RENK_MAP = {"📈 Güçlü Alım":"#16a34a","🔶 Orta Alım":"#84cc16","⬇️ Zayıf Alım":"#a3e635","📉 Güçlü Satım":"#dc2626","🔶 Orta Satım":"#f97316","⬆️ Zayıf Satım":"#fca5a5","⚖️ Belirsiz":"#9ca3af"};
                const haSF = (typeof filtre.sinyal_filtre==="object"&&!Array.isArray(filtre.sinyal_filtre)) ? filtre.sinyal_filtre : {};
                const toggleHA = s => set("sinyal_filtre",{...haSF,[s]:!haSF[s]});
                return (<>
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Ort. Pencere (Gün)</div>
                        <select value={filtre.lookback||20} onChange={e=>set("lookback",Number(e.target.value))} style={{padding:"6px 10px",borderRadius:"7px",border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:"13px",width:"100%"}}>
                            {[5,10,20,30,40,50].map(v=><option key={v} value={v}>{v} Gün</option>)}
                        </select>
                    </div>
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Min. Spike (×Ort.)</div>
                        <input type="number" value={filtre.min_spike||1.5} min={1.0} max={10} step={0.1} onChange={e=>set("min_spike",parseFloat(e.target.value))} style={{padding:"6px 10px",borderRadius:"7px",border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:"13px",width:"70px"}}/>
                    </div>
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Sinyal Tipi</div>
                        <div style={{display:"flex",gap:8}}>
                            <div style={{background:"#0f172a",border:"1px solid #16a34a55",borderRadius:6,padding:"6px 10px",flex:1}}>
                                <div style={{fontSize:10,fontWeight:700,color:"#16a34a",marginBottom:4}}>📈 ALIMLAR</div>
                                {["📈 Güçlü Alım","🔶 Orta Alım","⬇️ Zayıf Alım","⚖️ Belirsiz"].map(s=>{const aktif=!!haSF[s];return(<button key={s} type="button" onClick={()=>toggleHA(s)} style={{display:"block",width:"100%",textAlign:"left",padding:"2px 6px",borderRadius:4,marginBottom:2,cursor:"pointer",fontSize:11,fontWeight:aktif?700:400,background:aktif?HA_RENK_MAP[s]+"22":"transparent",border:`1px solid ${aktif?HA_RENK_MAP[s]:"transparent"}`,color:aktif?HA_RENK_MAP[s]:"#94a3b8"}}>{s}</button>);})}
                            </div>
                            <div style={{background:"#0f172a",border:"1px solid #dc262655",borderRadius:6,padding:"6px 10px",flex:1}}>
                                <div style={{fontSize:10,fontWeight:700,color:"#dc2626",marginBottom:4}}>📉 SATIMLAR</div>
                                {["📉 Güçlü Satım","🔶 Orta Satım","⬆️ Zayıf Satım"].map(s=>{const aktif=!!haSF[s];return(<button key={s} type="button" onClick={()=>toggleHA(s)} style={{display:"block",width:"100%",textAlign:"left",padding:"2px 6px",borderRadius:4,marginBottom:2,cursor:"pointer",fontSize:11,fontWeight:aktif?700:400,background:aktif?HA_RENK_MAP[s]+"22":"transparent",border:`1px solid ${aktif?HA_RENK_MAP[s]:"transparent"}`,color:aktif?HA_RENK_MAP[s]:"#94a3b8"}}>{s}</button>);})}
                            </div>
                        </div>
                    </div>
                </>);
            }
            case "Divergence Tarama": {
                const DIV_OSC = ["RSI","MACD Histogram","Stokastik RSI","OBV"];
                const DIV_TIPLER = [{key:"Regular Bullish",ikon:"📈",renk:"#16a34a"},{key:"Regular Bearish",ikon:"📉",renk:"#dc2626"},{key:"Hidden Bullish",ikon:"🔼",renk:"#06b6d4"},{key:"Hidden Bearish",ikon:"🔽",renk:"#f59e0b"}];
                const divOsc = filtre.osilatorler || {};
                const divTip = filtre.divergence_tipleri || {};
                const toggleOsc = o => set("osilatorler",{...divOsc,[o]:!divOsc[o]});
                const toggleTip = t => set("divergence_tipleri",{...divTip,[t]:!divTip[t]});
                const ayarlarAcik = filtre.ayarlar_acik||false;
                const stochAktif = !!divOsc["Stokastik RSI"], macdAktif = !!divOsc["MACD Histogram"], rsiAktif = !!divOsc["RSI"];
                return (<>
                    <Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} />
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Pivot Modu</div>
                        <div style={{display:"flex",gap:6}}>{[["fractal","Fractal"],["zigzag","ZigZag"]].map(([v,l])=>{const aktif=(filtre.pivot_modu||"fractal")===v;return(<button key={v} type="button" onClick={()=>set("pivot_modu",v)} style={{padding:"5px 12px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${aktif?"#3b82f6":"#334155"}`,background:aktif?"#3b82f622":"#0f172a",color:aktif?"#3b82f6":"#94a3b8"}}>{l}</button>);})}</div>
                    </div>
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Osilatörler</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>{DIV_OSC.map(o=>{const aktif=!!divOsc[o];return(<button key={o} type="button" onClick={()=>toggleOsc(o)} style={{padding:"4px 10px",borderRadius:8,fontSize:11,cursor:"pointer",fontWeight:aktif?600:400,border:`1px solid ${aktif?"#3b82f6":"#334155"}`,background:aktif?"#3b82f618":"#0f172a",color:aktif?"#3b82f6":"#94a3b8"}}>{o}</button>);})}</div>
                    </div>
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Divergence Tipleri</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>{DIV_TIPLER.map(t=>{const aktif=!!divTip[t.key];return(<button key={t.key} type="button" onClick={()=>toggleTip(t.key)} style={{padding:"4px 10px",borderRadius:8,fontSize:11,cursor:"pointer",fontWeight:aktif?600:400,border:`1px solid ${aktif?t.renk:"#334155"}`,background:aktif?t.renk+"18":"#0f172a",color:aktif?t.renk:"#94a3b8"}}>{t.ikon} {t.key}</button>);})}</div>
                    </div>
                    <button type="button" onClick={()=>set("ayarlar_acik",!ayarlarAcik)} style={{padding:"5px 12px",borderRadius:8,border:"1px solid #334155",background:ayarlarAcik?"#334155":"#0f172a",color:"#e2e8f0",fontSize:11,fontWeight:600,cursor:"pointer",marginBottom:8}}>⚙️ Gelişmiş Ayarlar {ayarlarAcik?"▲":"▼"}</button>
                    {ayarlarAcik && (<div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:8}}>
                        {(filtre.pivot_modu||"fractal")==="fractal"
                            ? (<div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>Swing Penceresi</div><input type="number" value={filtre.swing_pencere||5} min={2} max={20} onChange={e=>set("swing_pencere",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div>)
                            : (<div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>ZigZag Eşik (%)</div><input type="number" value={filtre.zigzag_esik||1} min={0.5} max={20} step={0.5} onChange={e=>set("zigzag_esik",parseFloat(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div>)
                        }
                        <div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>N Son Pivot</div><div style={{display:"flex",gap:4}}>{[2,3].map(n=>{const a=(filtre.n_son_pivot||2)===n;return(<button key={n} type="button" onClick={()=>set("n_son_pivot",n)} style={{padding:"4px 8px",borderRadius:6,fontSize:12,cursor:"pointer",border:`1px solid ${a?"#3b82f6":"#334155"}`,background:a?"#3b82f622":"#0f172a",color:a?"#3b82f6":"#94a3b8"}}>{n}</button>);})}</div></div>
                        <div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>Min Pivot Mesafe</div><input type="number" value={filtre.min_pivot_mesafe||3} min={0} max={20} onChange={e=>set("min_pivot_mesafe",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div>
                        <div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>Min Hareket (xATR)</div><input type="number" value={filtre.min_hareket_atr||0} min={0} max={5} step={0.1} onChange={e=>set("min_hareket_atr",parseFloat(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div>
                        {rsiAktif && <div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>RSI Periyot</div><input type="number" value={filtre.rsi_periyot||14} min={2} max={50} onChange={e=>set("rsi_periyot",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div>}
                        {macdAktif && <><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>MACD Fast</div><input type="number" value={filtre.macd_fast||12} min={2} max={50} onChange={e=>set("macd_fast",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>MACD Slow</div><input type="number" value={filtre.macd_slow||26} min={5} max={100} onChange={e=>set("macd_slow",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>MACD Signal</div><input type="number" value={filtre.macd_signal||9} min={2} max={50} onChange={e=>set("macd_signal",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div></>}
                        {stochAktif && <><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>StochRSI RSI Per.</div><input type="number" value={filtre.stoch_rsi_periyot||14} min={2} max={50} onChange={e=>set("stoch_rsi_periyot",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>StochRSI Stoch Per.</div><input type="number" value={filtre.stoch_periyot||14} min={2} max={50} onChange={e=>set("stoch_periyot",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>%K</div><input type="number" value={filtre.stoch_k||3} min={1} max={20} onChange={e=>set("stoch_k",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:55}}/></div><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>%D</div><input type="number" value={filtre.stoch_d||3} min={1} max={20} onChange={e=>set("stoch_d",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:55}}/></div></>}
                    </div>)}
                </>);
            }
            default: return null;
        }
    };

    return (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
            <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:"14px",padding:"1.75rem",width:"500px",maxWidth:"95vw",maxHeight:"85vh",overflowY:"auto",boxShadow:"0 12px 48px rgba(0,0,0,0.5)",color:"#e2e8f0"}}>
                <div style={{fontSize:"18px",fontWeight:"700",color:"#e2e8f0",marginBottom:"0.5rem"}}>🔗 Çoklu Taramaya Gönder</div>
                <p style={{fontSize:"13px",color:"#94a3b8",marginBottom:"1.25rem"}}>
                    <strong>{secilenSayisi}</strong> hisse seçili. Devam edilecek modülü ve kriterleri seçin.
                </p>
                <div style={{marginBottom:"1.25rem"}}>
                    <div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"8px"}}>Modül Seçin</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px"}}>
                        {COKLU_MODULLER.map(m => (
                            <button key={m.value} type="button" disabled={m.pasif} onClick={() => !m.pasif && onModulSec(m.value)}
                                style={{padding:"8px 10px",borderRadius:"8px",border:"2px solid",
                                    borderColor:secilenModul===m.value?"#3b82f6":m.pasif?"#1e293b":"#334155",
                                    background: secilenModul===m.value?"#1d4ed822":m.pasif?"#0f172a":"#0f172a",
                                    color:m.pasif?"#475569":secilenModul===m.value?"#93c5fd":"#e2e8f0",
                                    cursor:m.pasif?"default":"pointer",
                                    fontSize:"12px",fontWeight:secilenModul===m.value?"600":"400",textAlign:"left"}}>
                                {m.label}
                                {m.pasif && <span style={{fontSize:"10px",marginLeft:"4px",color:"#d1d5db"}}>(Mevcut)</span>}
                            </button>
                        ))}
                    </div>
                </div>
                {secilenModul && (
                    <div style={{background:"#0f172a",border:"1px solid #334155",borderRadius:"8px",padding:"10px 14px",marginBottom:"1rem",display:"flex",gap:"20px"}}>
                        <div><div style={{fontSize:"11px",color:"#9ca3af",fontWeight:"500"}}>Piyasa</div><div style={{fontSize:"13px",fontWeight:"600",color:"#e2e8f0"}}>🔒 {piyasa}</div></div>
                        <div><div style={{fontSize:"11px",color:"#9ca3af",fontWeight:"500"}}>Para Birimi</div><div style={{fontSize:"13px",fontWeight:"600",color:"#e2e8f0"}}>🔒 {paraBirimi}</div></div>
                    </div>
                )}
                {secilenModul && filtre && (
                    <div style={{marginBottom:"1.25rem"}}>
                        <div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"10px"}}>Tarama Kriterleri</div>
                        {renderFiltreler()}
                    </div>
                )}
                <div style={{display:"flex",gap:"8px",justifyContent:"flex-end"}}>
                    <button onClick={onIptal} style={{padding:"8px 18px",borderRadius:"8px",border:"1px solid #334155",background:"transparent",cursor:"pointer",fontSize:"13px",color:"#94a3b8"}}>İptal</button>
                    <button onClick={onBaslat} disabled={!secilenModul}
                        style={{padding:"8px 20px",borderRadius:"8px",border:"none",background:secilenModul?"linear-gradient(135deg,#1d4ed8,#3b82f6)":"#475569",color:"white",cursor:secilenModul?"pointer":"default",fontSize:"13px",fontWeight:"600"}}>
                        Taramayı Başlat →
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Hareketli Yükleme Çubuğu ────────────────────────────────────────────────
function HareketliBar() {
    const [offset, setOffset] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setOffset(o => (o+3)%200), 20);
        return () => clearInterval(id);
    }, []);
    const pos = offset<=100 ? offset-100 : 200-offset-100;
    return (
        <div style={{height:8,background:"#0f172a",borderRadius:4,overflow:"hidden",
            border:"1px solid #334155",marginBottom:16,position:"relative"}}>
            <div style={{position:"absolute",top:0,height:"100%",width:"40%",
                left:`${50+pos*0.5}%`,
                background:"linear-gradient(90deg,transparent,#3b82f6,#06b6d4,#3b82f6,transparent)",
                borderRadius:4}}/>
        </div>
    );
}

// ─── Bilgi İçerikleri ─────────────────────────────────────────────────────────
const BILGI_ICERIK = {
    "Skor": {
        baslik: "Giriş Skoru",
        icerik: "Trend(%20) + Hacim(%20) + Momentum(%15) + Formasyon(%15) + Smart Money(%20) + Destek/Direnç(%10) kriterlerinin ağırlıklı toplamından oluşan 0-100 arası puan. Piyasa rejimiyle birleştirilerek Master Skor'a dönüşür.",
    },
    "Sınıf": {
        baslik: "Sınıf",
        icerik: "Skora göre atanan harf notu: 90+ → A+, 80-89 → A, 70-79 → B, 60-69 → C, altı sınıflandırılmaz. A+ ve A sınıfı fırsatlar en güçlü teyitli sinyalleri temsil eder.",
    },
    "Karar": {
        baslik: "Karar",
        icerik: "Skor, sınıf ve piyasa rejimine göre üretilen net eylem önerisi: GÜÇLÜ AL, AL, İZLE, SAT, GÜÇLÜ SAT. Piyasa rejimine aykırı sinyaller (ör. ayı piyasasında long) otomatik olarak zayıflatılır.",
    },
    "Stop": {
        baslik: "Stop Loss",
        icerik: "Pozisyonun geçersiz sayılacağı, riskin sınırlandığı seviye. Long'da fiyatın altına, Short'ta üstüne yerleştirilir; genellikle son swing seviyesi veya ATR bazlı hesaplanır.",
    },
    "Hedef1": {
        baslik: "Hedef 1",
        icerik: "Teknik araçlarla hesaplanan, fiyatın ulaşması beklenen ilk (en yakın/muhafazakâr) hedef seviyesi.",
    },
    "Kazanç1": {
        baslik: "Kazanç 1",
        icerik: "Güncel fiyattan Hedef 1'e ulaşıldığında elde edilecek yüzdesel kazanç (Long) veya kayıp korunumu (Short).",
    },
    "Hedef2": {
        baslik: "Hedef 2",
        icerik: "İlk hedefin ötesinde, daha güçlü/uzun bir hareket senaryosunda ulaşılabilecek ikinci (daha iyimser) hedef seviyesi.",
    },
    "Kazanç2": {
        baslik: "Kazanç 2",
        icerik: "Güncel fiyattan Hedef 2'ye ulaşıldığında elde edilecek yüzdesel kazanç.",
    },
    "RSI": {
        baslik: "RSI (Relative Strength Index)",
        icerik: "Fiyatın son dönemdeki yükseliş/düşüş gücünü 0-100 arası ölçen momentum osilatörü. 70 üzeri aşırı alım, 30 altı aşırı satım bölgesi olarak değerlendirilir.",
    },
    "R/Ö H1": {
        baslik: "Risk/Ödül — Hedef 1",
        icerik: "Hedef 1'e göre hesaplanan risk/ödül oranı — potansiyel kazancın, stop'a olan potansiyel kayba oranı. 1:2 ve üzeri değerler genellikle tercih edilir.",
    },
    "H2": {
        baslik: "Risk/Ödül — Hedef 2",
        icerik: "Hedef 2'ye (daha uzak/iyimser hedefe) göre hesaplanan risk/ödül oranı.",
    },
    "ATR": {
        baslik: "ATR (Average True Range)",
        icerik: "Son dönemin ortalama gerçek aralığı — piyasanın güncel volatilitesini ölçer. Stop-loss ve hedef mesafelerinin hesaplanmasında baz alınır.",
    },
    "EMA20": {
        baslik: "EMA20 (20 Periyot Üstel Hareketli Ortalama)",
        icerik: "Son 20 periyodun üstel ağırlıklı ortalama fiyatı. Kısa vadeli trend yönünü gösterir; fiyatın EMA20 üzerinde olması kısa vadeli boğa eğilimine işaret eder.",
    },
    "EMA50": {
        baslik: "EMA50 (50 Periyot Üstel Hareketli Ortalama)",
        icerik: "Son 50 periyodun üstel ağırlıklı ortalama fiyatı. Orta vadeli trend yönünü gösterir; EMA20'nin EMA50 üzerinde olması (golden cross benzeri) güçlü bir trend teyididir.",
    },
    "Trend": {
        baslik: "Trend",
        icerik: "Fiyatın hareketli ortalamalara (EMA20/EMA50) göre konumu ve yönü. Güçlü ve net bir trend yapısı, sinyalin güvenilirliğini artıran en önemli faktörlerden biridir.",
    },
    "Hacim": {
        baslik: "Hacim",
        icerik: "İşlem hacminin ortalamaya göre durumu. Fiyat hareketiyle birlikte artan hacim, hareketin gerçek alım/satım ilgisiyle desteklendiğini gösterir.",
    },
    "Momentum": {
        baslik: "Momentum (RSI+MACD)",
        icerik: "RSI ve MACD göstergelerinin birleşik değerlendirmesi. Fiyat hareketinin hızını ve gücünü ölçer; momentum ile fiyat yönünün uyumlu olması sinyali güçlendirir.",
    },
    "Formasyon": {
        baslik: "Formasyon",
        icerik: "Üçgen, bayrak, konsolidasyon gibi klasik grafik formasyonlarının tespiti. Bu formasyonlar genellikle bir kırılım öncesi biriken enerjiyi gösterir.",
    },
    "Smart Money": {
        baslik: "Smart Money",
        icerik: "FVG (Fair Value Gap), Order Block ve likidite seviyeleri gibi kurumsal/akıllı para izlerinin tespiti. Bu yapıların fiyata yakınlığı, kurumsal ilginin olası olduğu bölgeleri işaret eder.",
    },
    "Destek/Direnç": {
        baslik: "Destek/Direnç",
        icerik: "Fiyatın önemli geçmiş destek ve direnç seviyelerine olan yakınlığı. Bu seviyelere yakın işlemler, tepki verme ihtimali nedeniyle daha dikkatli değerlendirilir.",
    },
	"Sektör Gücü": {
        baslik: "Sektör Gücü",
        icerik: "Hissenin ait olduğu sektörün (veya coinin kategori endeksinin), genel piyasaya göre görece performansı. Güçlü bir sektörde yer alan semboller daha yüksek başarı potansiyeline sahiptir; bu puan ana skora bir modifikatör (+/-) olarak eklenir.",
    },
    "Karşı-Sinyal Veto": {
        baslik: "Karşı-Sinyal Veto",
        icerik: "Teknik skor AL/GÜÇLÜ AL üretse bile, o sembole özel Haber Duygusu güçlü negatifse, Ayı SMT uyumsuzluğu varsa veya sembolün sektör/kategori rejimi düşüşteyse sinyal tamamen iptal edilir (skor düşürülmez, karar İZLE'ye çevrilir). Amaç: teknik olarak güçlü görünen ama temel/piyasa bağlamıyla çelişen sinyalleri elemek.",
    },
};

function BilgiIkonu({ alan }) {
    const [acik, setAcik] = useState(false);
    const bilgi = BILGI_ICERIK[alan];
    if (!bilgi) return null;
    return (
        <span style={{position:"relative",display:"inline-flex",alignItems:"center",marginLeft:4}}>
            <span
                onMouseEnter={() => setAcik(true)}
                onMouseLeave={() => setAcik(false)}
                style={{cursor:"help",color:"#94a3b8",fontSize:11,
                    border:"1px solid #334155",borderRadius:"50%",
                    width:14,height:14,display:"inline-flex",alignItems:"center",
                    justifyContent:"center",fontWeight:700,lineHeight:1}}>
                i
            </span>
            {acik && (
                <div style={{
                    position:"absolute",top:"calc(100% + 6px)",left:"50%",
                    transform:"translateX(-50%)",zIndex:500,
                    background:"#1e293b",border:"1px solid #334155",
                    borderRadius:10,padding:"12px 14px",width:270,
                    boxShadow:"0 8px 24px rgba(0,0,0,0.5)",pointerEvents:"none",
                }}>
                    <div style={{fontSize:12,fontWeight:700,color:"#3b82f6",marginBottom:6}}>{bilgi.baslik}</div>
                    <div style={{fontSize:11,color:"#94a3b8",lineHeight:1.6,whiteSpace:"pre-line"}}>{bilgi.icerik}</div>
                </div>
            )}
        </span>
    );
}

function SkorCubugu({ puan, max, etiket, bilgiAlan }) {
    const pct = Math.min(100, (puan / max) * 100);
    return (
        <div style={{ marginBottom: "7px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "3px" }}>
                <span style={{ color: "#e2e8f0", fontWeight: 500, display: "flex", alignItems: "center" }}>{etiket}{bilgiAlan && <BilgiIkonu alan={bilgiAlan}/>}</span>
                <span style={{ color: skorRenk(pct), fontWeight: 700 }}>{puan}/{max}</span>
            </div>
            <div style={{ height: "5px", background: "#334155", borderRadius: "99px", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, borderRadius: "99px", transition: "width 0.4s",
                    background: pct >= 80 ? "#22c55e" : pct >= 50 ? "#3b82f6" : "#f59e0b" }} />
            </div>
        </div>
    );
}

function ModifikatorCubugu({ puan, etiket, bilgiAlan }) {
    // ±5 aralığını ortadan (0 noktası merkez) gösteren özel çubuk —
    // SkorCubugu'dan farklı: negatif değerler de anlamlı şekilde görselleşir.
    const maks = 5;
    const pctSag = puan > 0 ? Math.min(100, (puan / maks) * 100) : 0;
    const pctSol = puan < 0 ? Math.min(100, (Math.abs(puan) / maks) * 100) : 0;
    const renk = puan > 0 ? "#22c55e" : puan < 0 ? "#dc2626" : "#94a3b8";
    return (
        <div style={{ marginBottom: "7px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "3px" }}>
                <span style={{ color: "#e2e8f0", fontWeight: 500, display: "flex", alignItems: "center" }}>{etiket}{bilgiAlan && <BilgiIkonu alan={bilgiAlan}/>}</span>
                <span style={{ color: renk, fontWeight: 700 }}>
                    {puan > 0 ? "+" : ""}{puan}
                </span>
            </div>
            <div style={{ height: "5px", background: "#334155", borderRadius: "99px", overflow: "hidden",
                display: "flex", position: "relative" }}>
                {/* Sol yarı: negatif değer alanı (sağdan sola dolar) */}
                <div style={{ width: "50%", height: "100%", display: "flex", justifyContent: "flex-end" }}>
                    <div style={{ height: "100%", width: `${pctSol}%`, background: "#dc2626", transition: "width 0.4s" }} />
                </div>
                {/* Sağ yarı: pozitif değer alanı (soldan sağa dolar) */}
                <div style={{ width: "50%", height: "100%" }}>
                    <div style={{ height: "100%", width: `${pctSag}%`, background: "#22c55e", transition: "width 0.4s" }} />
                </div>
                {/* Orta çizgi (0 noktası) */}
                <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: "1px", background: "#94a3b8" }} />
            </div>
        </div>
    );
}

// ─── Piyasa Rejimi Kartı ─────────────────────────────────────────────────────

function PiyasaRejimiKart({ rejim }) {
    if (!rejim) return null;
    const renk = rejimRenk(rejim.rejim);
    return (
        <div style={{ background: "#1e293b", borderRadius: "12px", padding: "14px 16px",
            border: `2px solid ${renk}25`,
            boxShadow: "0 1px 4px rgba(0,0,0,0.07)", height: "100%", boxSizing: "border-box" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <div>
                    <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "2px" }}>Piyasa Rejimi</div>
                    <div style={{ fontSize: "17px", fontWeight: 800, color: renk }}>{rejim.rejim}</div>
                </div>
                <div style={{ textAlign: "center", background: skorBg(rejim.puan),
                    padding: "6px 14px", borderRadius: "10px" }}>
                    <div style={{ fontSize: "24px", fontWeight: 900, color: skorRenk(rejim.puan), lineHeight: 1 }}>
                        {rejim.puan}
                    </div>
                    <div style={{ fontSize: "10px", color: "#94a3b8" }}>/100</div>
                </div>
            </div>
            <div style={{ fontSize: "12px", color: "#e2e8f0", background: "#182234",
                padding: "7px 11px", borderRadius: "7px", marginBottom: "8px" }}>
                📋 Önerilen Strateji: <strong>{rejim.strateji}</strong>
            </div>
            {rejim.kriterler && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                    {Object.values(rejim.kriterler).map((v, i) => (
                        <span key={i} style={{ fontSize: "11px", padding: "2px 8px", borderRadius: "20px", fontWeight: 500,
                            background: v.renk === "green" ? "#16a34a22" : v.renk === "red" ? "#dc262622" : "#f59e0b22",
                            color: v.renk === "green" ? "#15803d" : v.renk === "red" ? "#dc2626" : "#92400e" }}>
                            {v.detay}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Zaman Dilimi Kartı ───────────────────────────────────────────────────────

function ZamanDilimiKart({ vade }) {
    const map = ZAMAN_DİLİMLERİ[vade];
    if (!map) return null;
    return (
        <div style={{ background: "#1e293b", borderRadius: "10px", padding: "10px 14px",
            border: "1px solid #334155" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#93c5fd", marginBottom: "7px" }}>
                📐 {vade} — Çoklu Zaman Dilimi
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
                {Object.entries(map).map(([k, v]) => (
                    <div key={k} style={{ background: "#0f172a", borderRadius: "7px", padding: "5px 11px",
                        border: "1px solid #334155", fontSize: "12px" }}>
                        <span style={{ color: "#94a3b8" }}>{k}: </span>
                        <strong style={{ color: "#93c5fd" }}>{v}</strong>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Detay Panel (sağdan açılan) ─────────────────────────────────────────────

// ─── İlerleme Paneli ─────────────────────────────────────────────────────────
function TaramaIlerlemePaneli({ ilerleme, tamamlanan, toplam, bulunan, gecen, tahminiSure }) {
    const fmt = sn => {
        if (!sn || sn <= 0) return "—";
        const m = Math.floor(sn / 60), sec = sn % 60;
        return m === 0 ? `${sec}s` : `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    };
    let kalan = tahminiSure || null;
    if (!kalan && tamamlanan > 0 && gecen > 0 && toplam > 0) {
        kalan = Math.round((gecen / tamamlanan) * (toplam - tamamlanan));
    }
    return (
        <div style={{ marginBottom: 16, background: "#1e293b", border: "2px solid #3b82f644",
            borderRadius: 12, padding: 20, boxShadow: "0 0 20px #3b82f618" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: 20 }}>🧭</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: "#e2e8f0" }}>Karar Motoru Taraması Çalışıyor</span>
                <span style={{ marginLeft: "auto", fontSize: 22, fontWeight: 800, color: "#3b82f6" }}>%{ilerleme}</span>
            </div>
            <div style={{ height: 10, background: "#0f172a", borderRadius: 6, overflow: "hidden",
                marginBottom: 10, border: "1px solid #334155" }}>
                <div style={{ height: "100%", width: `${ilerleme}%`, background: "linear-gradient(90deg,#3b82f6,#06b6d4)",
                    borderRadius: 6, transition: "width 0.5s ease" }} />
            </div>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 2 }}>TAMAMLANAN</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#3b82f6" }}>
                        {toplam > 0 ? `${(tamamlanan||0).toLocaleString("tr-TR")} / ${toplam.toLocaleString("tr-TR")}` : "—"}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 2 }}>BULUNAN</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#16a34a" }}>
                        {bulunan > 0 ? bulunan.toLocaleString("tr-TR") : "—"}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 2 }}>GEÇEN SÜRE</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#0d9488" }}>{fmt(gecen)}</div>
                </div>
                <div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 2 }}>TAHMİNİ KALAN</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#eab308" }}>
                        {kalan != null ? fmt(kalan) : (tamamlanan === 0 ? "Hesaplanıyor..." : "—")}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── İndir Alt Menüsü ─────────────────────────────────────────────────────────
function IndirMenusu({ veriler, setIslemMenuAcik }) {
    const [acik, setAcik] = useState(false);
    const dosyaAdi = `karar_motoru_${new Date().toLocaleDateString("tr-TR").replace(/\./g,"-")}`;
    const indir = async (fmt) => {
        setAcik(false); setIslemMenuAcik(false);
        const veri = veriler();
        if (!veri?.length) return;
        if (fmt === "xlsx") {
            const ws = XLSX.utils.json_to_sheet(veri);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Karar Motoru");
            XLSX.writeFile(wb, `${dosyaAdi}.xlsx`);
        } else if (fmt === "csv") {
            const h = Object.keys(veri[0] || {});
            const rows = veri.map(r => h.map(k => { const v = String(r[k] ?? ""); return v.includes(",")||v.includes('"') ? `"${v.replace(/"/g,'""')}"` : v; }).join(","));
            const blob = new Blob(["\uFEFF"+[h.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = `${dosyaAdi}.csv`; a.click(); URL.revokeObjectURL(url);
        } else if (fmt === "pdf") {
            try {
                const { default: jsPDF } = await import("jspdf");
                const { default: autoTable } = await import("jspdf-autotable");
                const h = Object.keys(veri[0] || {});
                const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
                doc.setFontSize(14); doc.text("Karar Motoru Sonuçları", 20, 30);
                doc.setFontSize(9); doc.setTextColor(100);
                doc.text(`${veri.length} kayıt · ${new Date().toLocaleDateString("tr-TR")}`, 20, 48);
                autoTable(doc, { head: [h], body: veri.map(r => h.map(k => String(r[k] ?? ""))), startY: 58,
                    styles: { fontSize: 7, cellPadding: 3 },
                    headStyles: { fillColor: [29,78,216], textColor: 255, fontStyle: "bold" },
                    alternateRowStyles: { fillColor: [245,247,250] } });
                doc.save(`${dosyaAdi}.pdf`);
            } catch { toast.error("PDF oluşturulamadı"); indir("svg"); }
        } else if (fmt === "svg") {
            const h = Object.keys(veri[0] || {});
            const cw = Math.max(60, Math.floor(740/h.length)), rh = 18, hh = 24, tw = cw*h.length;
            const pw = Math.max(tw+40, 800), ph = 80 + hh + veri.length*rh;
            const esc = s => String(s).slice(0,18).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
            const rows = veri.map((r,i) => `<g transform="translate(20,${60+hh+i*rh})"><rect width="${tw}" height="${rh}" fill="${i%2===0?"#1e293b":"#0f172a"}" stroke="#334155" stroke-width="0.5"/>${h.map((k,j) => `<text x="${j*cw+4}" y="13" font-size="9" fill="#e2e8f0" font-family="Arial">${esc(r[k])}</text>`).join("")}</g>`).join("");
            const heads = h.map((k,j) => `<text x="${20+j*cw+4}" y="48" font-size="9" font-weight="bold" fill="white" font-family="Arial">${esc(k)}</text>`).join("");
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${pw}" height="${ph}"><rect width="${pw}" height="${ph}" fill="#0f172a"/><text x="20" y="22" font-size="14" font-weight="bold" fill="#e2e8f0" font-family="Arial">Karar Motoru Sonuçları</text><text x="20" y="38" font-size="10" fill="#94a3b8" font-family="Arial">${veri.length} kayıt · ${new Date().toLocaleDateString("tr-TR")}</text><rect x="20" y="52" width="${tw}" height="${hh}" fill="#1d4ed8"/>${heads}${rows}</svg>`;
            const blob = new Blob([svg], { type: "image/svg+xml" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = `${dosyaAdi}.svg`; a.click(); URL.revokeObjectURL(url);
        }
    };
    return (
        <div style={{ position: "relative" }} onMouseEnter={() => setAcik(true)} onMouseLeave={() => setAcik(false)}>
            <button style={{ display: "flex", width: "100%", textAlign: "left", justifyContent: "space-between",
                padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
                color: "#e2e8f0", fontSize: 13, borderBottom: "1px solid #334155" }}>
                📥 İndir <span style={{ color: "#94a3b8" }}>▶</span>
            </button>
            {acik && (
                <div style={{ position: "absolute", left: "100%", top: 0, background: "#1e293b",
                    border: "1px solid #334155", borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.4)", minWidth: 160, overflow: "hidden", zIndex: 101 }}>
                    {[{l:"Excel (.xlsx)",f:"xlsx"},{l:"CSV (.csv)",f:"csv"},{l:"PDF (.pdf)",f:"pdf"},{l:"SVG (.svg)",f:"svg"}].map(b => (
                        <button key={b.f} onClick={() => indir(b.f)}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px",
                                border: "none", background: "none", cursor: "pointer", color: "#e2e8f0", fontSize: 13,
                                borderBottom: "1px solid #334155" }}>{b.l}</button>
                    ))}
                </div>
            )}
        </div>
    );
}

function DetayPanel({ r, onKapat, onSimulasyon }) {
    if (!r) return null;
    const d = r.detay || {};
    const kazanc1 = yuzde(r.fiyat, r.hedef1);
    const kazanc2 = yuzde(r.fiyat, r.hedef2);

    return (
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "400px",
            background: "#1e293b", boxShadow: "-4px 0 24px rgba(0,0,0,0.13)",
            zIndex: 1000, overflowY: "auto", padding: "18px" }}>

            {/* Başlık */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
                <div>
                    <div style={{ fontSize: "20px", fontWeight: 800, color: "#93c5fd" }}>{r.sembol}</div>
                    <div style={{ fontSize: "12px", color: "#94a3b8" }}>{r.vade} · {r.mod}</div>
                </div>
                <button onClick={onKapat} style={{ background: "#334155", border: "none",
                    borderRadius: "7px", padding: "6px 10px", cursor: "pointer", fontSize: "15px" }}>✕</button>
            </div>

            {/* Ana skor */}
            <div style={{ background: skorBg(r.giris_skoru, r.yon), borderRadius: "12px",
                padding: "14px", marginBottom: "14px", textAlign: "center" }}>
                <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "2px" }}>
                    {r.yon === "short" ? "📉 Short Sinyal Skoru" : "📈 Long Sinyal Skoru"}
                </div>
                <div style={{ fontSize: "38px", fontWeight: 900, color: skorRenk(r.giris_skoru, r.yon), lineHeight: 1.1 }}>
                    {r.giris_skoru}
                </div>
                <div style={{ fontSize: "13px", fontWeight: 700, color: kararRenk(r.karar), marginTop: "3px" }}>
                    {r.karar}
                </div>
				<div style={{ marginTop: "5px" }}>
                    <span style={{ ...sinifRenk(r.sinif), padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 700 }}>
                        {r.sinif}
                    </span>
                </div>
            </div>

            {/* Karşı-Sinyal Veto */}
            {r.veto_detay && r.veto_detay.length > 0 && (
                <div style={{ background: "#dc262622", borderRadius: "10px", padding: "10px 13px",
                    marginBottom: "14px", border: "1px solid #dc262644" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#dc2626", marginBottom: "6px",
                        display: "flex", alignItems: "center" }}>
                        🚫 Karşı-Sinyal Veto<BilgiIkonu alan="Karşı-Sinyal Veto"/>
                    </div>
                    {r.veto_detay.map((v, i) => (
                        <div key={i} style={{ fontSize: "11px", color: "#e2e8f0", padding: "4px 0",
                            borderTop: i > 0 ? "1px solid #dc262633" : "none" }}>
                            <span style={{ color: "#fca5a5", fontWeight: 600 }}>{v.kontrol}:</span> {v.detay}
                        </div>
                    ))}
                </div>
            )}

            {/* Fiyat / Stop / Hedefler */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "12px" }}>
                {[
                    { l: "Güncel Fiyat",
                      v: r.fiyat, c: "#2563eb", pct: null },
                    { l: r.yon === "short" ? "Stop (Üst)" : "Stop Loss",
                      v: r.stop_loss,
                      c: r.yon === "short" ? "#dc2626" : "#dc2626",
                      pct: r.yon === "short" ? yuzde(r.fiyat, r.stop_loss) : yuzde(r.fiyat, r.stop_loss) },
                    { l: r.yon === "short" ? "Hedef 1 (Aşağı)" : "Hedef 1",
                      v: r.hedef1,
                      c: r.yon === "short" ? "#dc2626" : "#16a34a",
                      pct: kazanc1 },
                    { l: r.yon === "short" ? "Hedef 2 (Aşağı)" : "Hedef 2",
                      v: r.hedef2,
                      c: r.yon === "short" ? "#991b1b" : "#059669",
                      pct: kazanc2 },
                ].map(item => (
                    <div key={item.l} style={{ background: "#182234", borderRadius: "8px",
                        padding: "9px", textAlign: "center", border: "1px solid #334155" }}>
                        <div style={{ fontSize: "10px", color: "#94a3b8", marginBottom: "2px" }}>{item.l}</div>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: item.c }}>{item.v ?? "—"}</div>
                        {item.pct !== null && item.pct !== undefined && (
                            <div style={{ fontSize: "11px", fontWeight: 600,
                                color: parseFloat(item.pct) >= 0 ? "#16a34a" : "#dc2626" }}>
                                {parseFloat(item.pct) >= 0 ? "+" : ""}{item.pct}%
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Risk/Ödül + ATR */}
            {(r.rr1 || r.rr2 || r.atr) && (
                <div style={{ background: "#16a34a22", borderRadius: "8px", padding: "9px 13px",
                    marginBottom: "12px", fontSize: "12px", border: "1px solid #16a34a44",
                    display: "flex", gap: "14px", flexWrap: "wrap" }}>
                    {r.rr1 && <span><strong style={{ color: "#15803d" }}>R/Ö H1<BilgiIkonu alan="R/Ö H1"/>: {r.rr1}:1</strong></span>}
                    {r.rr2 && <span style={{ color: "#94a3b8" }}>H2<BilgiIkonu alan="H2"/>: {r.rr2}:1</span>}
                    {r.atr && <span style={{ color: "#94a3b8" }}>ATR<BilgiIkonu alan="ATR"/>: {r.atr}</span>}
                </div>
            )}

            {/* EMA + RSI */}
            <div style={{ marginBottom: "12px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#e2e8f0", marginBottom: "6px" }}>
                    📈 Göstergeler
                </div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {r.ema20 && <span style={{ fontSize: "11px", padding: "3px 9px", background: "#334155", borderRadius: "6px" }}>EMA20<BilgiIkonu alan="EMA20"/>: <strong>{r.ema20}</strong></span>}
                    {r.ema50 && <span style={{ fontSize: "11px", padding: "3px 9px", background: "#334155", borderRadius: "6px" }}>EMA50<BilgiIkonu alan="EMA50"/>: <strong>{r.ema50}</strong></span>}
                    {r.rsi && (
                        <span style={{ fontSize: "11px", padding: "3px 9px", borderRadius: "6px",
                            background: r.rsi > 70 ? "#dc262622" : r.rsi < 30 ? "#16a34a22" : "#334155",
                            color: r.rsi > 70 ? "#dc2626" : r.rsi < 30 ? "#16a34a" : "#e2e8f0" }}>
                            RSI<BilgiIkonu alan="RSI"/>: <strong>{r.rsi}</strong>
                        </span>
                    )}
                </div>
            </div>

            {/* Faktör puanları */}
            <div style={{ marginBottom: "14px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#e2e8f0", marginBottom: "8px" }}>🎯 Faktör Puanları</div>
                <div style={{ background: "#182234", borderRadius: "10px", padding: "12px" }}>
                    <SkorCubugu puan={d.trend?.puan        || 0} max={20} etiket="📈 Trend" bilgiAlan="Trend" />
                    <SkorCubugu puan={d.hacim?.puan        || 0} max={20} etiket="📊 Hacim" bilgiAlan="Hacim" />
                    <SkorCubugu puan={d.momentum?.puan     || 0} max={15} etiket="⚡ Momentum (RSI+MACD)" bilgiAlan="Momentum" />
                    <SkorCubugu puan={d.formasyon?.puan    || 0} max={15} etiket="🔷 Formasyon" bilgiAlan="Formasyon" />
                    <SkorCubugu puan={d.smart_money?.puan  || 0} max={20} etiket="💰 Smart Money" bilgiAlan="Smart Money" />
                    <SkorCubugu puan={d.destek_direnc?.puan|| 0} max={10} etiket="🏔️ Destek/Direnç" bilgiAlan="Destek/Direnç" />
					{d.sektor_rs && d.sektor_rs.rs_skoru !== null && d.sektor_rs.rs_skoru !== undefined && (
                        <>
                            <div style={{ height: "1px", background: "#334155", margin: "8px 0" }} />
                            <ModifikatorCubugu
                                puan={d.sektor_rs.modifikator || 0}
                                etiket={`🔄 Sektör Gücü (${d.sektor_rs.endeks_kod || "—"})`}
                                bilgiAlan="Sektör Gücü"
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Analiz notları */}
            <div style={{ marginBottom: "14px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#e2e8f0", marginBottom: "7px" }}>📋 Analiz Notları</div>
                {[
                    { k: "trend",         l: "Trend",         icon: "📈" },
                    { k: "hacim",         l: "Hacim",         icon: "📊" },
                    { k: "momentum",      l: "Momentum",      icon: "⚡" },
                    { k: "formasyon",     l: "Formasyon",     icon: "🔷" },
                    { k: "smart_money",   l: "Smart Money",   icon: "💰" },
                    { k: "destek_direnc", l: "Destek/Direnç", icon: "🏔️" },
					{ k: "sektor_rs",     l: "Sektör Gücü",   icon: "🔄" },
                ].map(item => d[item.k] && (
                    <div key={item.k} style={{ display: "flex", gap: "7px", padding: "5px 0",
                        borderBottom: "1px solid #334155", fontSize: "12px" }}>
                        <span>{item.icon}</span>
                        <span style={{ color: "#94a3b8", minWidth: "88px" }}>{item.l}:</span>
                        <span style={{ color: "#e2e8f0", fontWeight: 500 }}>
                            {d[item.k].durum || d[item.k].ad || d[item.k].detay || "—"}
                        </span>
                    </div>
                ))}
            </div>

            {/* Simülasyona gönder */}
            <button onClick={() => onSimulasyon(r)} style={{ width: "100%", padding: "11px", background: "#0f172a", color: "white",
                border: "none", borderRadius: "9px", cursor: "pointer", fontSize: "13px", fontWeight: 700 }}>
                🔮 Geçmiş Simülasyonuna Gönder
            </button>
        </div>
    );
}

// ─── ANA COMPONENT ────────────────────────────────────────────────────────────

export default function KararMotoru() {
    const navigate = useNavigate();

    const [gecmisPopupAdim,  setGecmisPopupAdim]  = useState(null);
    const [gecmisSimTarihi,  setGecmisSimTarihi]  = useState(() => {
        const d = new Date(); d.setFullYear(d.getFullYear() - 1);
        return d.toISOString().split("T")[0];
    });
    const [gecmisRefFiyat,   setGecmisRefFiyat]   = useState("Bugünkü");
    const [gecmisMinSkor,   setGecmisMinSkor]   = useState(0);
    const [gecmisDetayHisse, setGecmisDetayHisse] = useState(null);

    // Filtre state'leri
    const [cokluPopup,     setCokluPopup]     = useState(false);
    const [cokluOnayPopup, setCokluOnayPopup] = useState(false);
    const [cokluSecModul,  setCokluSecModul]  = useState(null);
    const [cokluFiltre,    setCokluFiltre]    = useState(null);
    const [cokluPeriyotlar,setCokluPeriyotlar]= useState([]);

    const [piyasa,     setPiyasa]     = useState("BIST");
    const [vade,       setVade]       = useState("Kısa Vade (1-10 gün)");
    const [paraBirimi, setParaBirimi] = useState("TL");
    const [minSkor,    setMinSkor]    = useState(60);
    const [piyasaSecenekleri,     setPiyasaSecenekleri]     = useState(["BIST","Kripto"]);
    const [paraBirimiSecenekleri, setParaBirimiSecenekleri] = useState(["TL","USD"]);

    // Tarama state'leri
    const [calisiyor,  setCalisiyor]  = useState(false);
    const [ilerleme,   setIlerleme]   = useState(0);
    const [tahminiSure,setTahminiSure]= useState(null);
    const [sonuc,      setSonuc]      = useState(null);

    // UI state'leri
    const [detayAcik,  setDetayAcik]  = useState(null);
    const [siralama,   setSiralama]   = useState({ kol: "giris_skoru", yon: "desc" });
    const [anlikRejim, setAnlikRejim] = useState(null);
    const [rejimYukleniyor, setRejimYukleniyor] = useState(false);

    // ── Filtreler (sonuç listesi için) ───────────────────────────────────────
    const [filtreSinif,  setFiltreSinif]  = useState("Tümü");  // A+/A/B/C/D/Tümü
    const [filtreYon,    setFiltreYon]    = useState("Tümü");  // long/short/Tümü
    const [filtreKarar,  setFiltreKarar]  = useState("Tümü");  // AL/SAT/İZLE/Tümü

    // Checkbox + İşlemler menüsü
    const [secilenler,      setSecilenler]      = useState(new Set());
    const [islemMenuAcik,   setIslemMenuAcik]   = useState(false);
    const [alarmPopup,      setAlarmPopup]       = useState(false);
    const [alarmHisseler,   setAlarmHisseler]    = useState([]);
    const [hedefFiyatlar,   setHedefFiyatlar]    = useState({});
    const [alarmMail,       setAlarmMail]        = useState(false);
    const [alarmBildirim,   setAlarmBildirim]    = useState(true);
    const [alarmKaydediliyor, setAlarmKaydediliyor] = useState(false);
    const [dogrulukKaydediliyor, setDogrulukKaydediliyor] = useState(false);
    const [dogrulukPopup,        setDogrulukPopup]        = useState(null); // null | "yukleniyor" | "tamam"
    const [dogrulukSonuc,        setDogrulukSonuc]        = useState(null);
    const [puanlamaPopupAdim, setPuanlamaPopupAdim] = useState(null); // null | "kriter" | "onay"
    const [puanlamaVade,      setPuanlamaVade]      = useState(null);
    const [puanlamaMinPuan,   setPuanlamaMinPuan]   = useState(0);

    const [, setDurumMesaj]     = useState(null);
    const [bulunanSayisi,   setBulunanSayisi]   = useState(0);
    const [tamamlananSayisi,setTamamlananSayisi]= useState(0);
    const [toplamSayisi,    setToplamSayisi]    = useState(0);
    const [gecenSure,       setGecenSure]       = useState(0);   // saniye cinsinden
    const gecenSureRef = useRef(null);

    const intervalRef = useRef(null);
    const islemMenuRef = useRef(null);

    // ── Kriter seçeneklerini DB'den al ───────────────────────────────────────
    useEffect(() => {
        getLookupPiyasalar().then(p => { if (p?.length) setPiyasaSecenekleri(p); }).catch(e => console.log("Lookup hatası:", e));
    }, []);

    const piyasaRef = useRef(piyasa);
    useEffect(() => { piyasaRef.current = piyasa; }, [piyasa]);

    useEffect(() => {
        const bu_piyasa = piyasa;
        getLookupParaBirimleri(piyasa, "karar_motoru")
            .then(pb => {
                if (piyasaRef.current !== bu_piyasa) return; // eski/gecikmiş yanıt, yok say
                const liste = (pb || []).map(x => (typeof x === "string" ? x : x?.para_birimi ?? x?.value ?? "")).map(x => String(x).trim()).filter(Boolean);
                if (!liste.length) {
                    console.warn(`[Karar Motoru] para-birimleri (${piyasa}) DB'den boş geldi, varsayılana düşülüyor.`);
                    const varsayilan = piyasa === "BIST" ? ["TL","USD"] : ["USD","USD (Futures)"];
                    setParaBirimiSecenekleri(varsayilan);
                    if (!varsayilan.includes(paraBirimi)) setParaBirimi(varsayilan[0]);
                    return;
                }
                setParaBirimiSecenekleri(liste);
                if (!liste.includes(paraBirimi)) setParaBirimi(liste.includes("USD") ? "USD" : liste[0]);
            })
            .catch(e => console.log("Lookup hatası:", e));
    }, [piyasa]);

    // ── Piyasa değişince vade sıfırla + rejim yenile (para birimi artık DB lookup effect'inde yönetiliyor) ──
    useEffect(() => {
        const vOpts = VADE_OPTS[piyasa];
        if (!vOpts.includes(vade)) setVade(vOpts[0]);
        // Sonuç olsa dahi rejimi temizle ve yenile
        setAnlikRejim(null);
        rejimYukle(piyasa);
    }, [piyasa]);

    // ── USD (Futures) seçilince Kaldıraçlı İşlem'e kilitle; USD'ye dönünce Gün İçi'ne geç ──
    useEffect(() => {
        if (piyasa !== "Kripto") return;
        if (paraBirimi === "USD (Futures)") {
            setVade("Kaldıraçlı İşlem");
        } else if (vade === "Kaldıraçlı İşlem") {
            setVade("Gün İçi");
        }
    }, [paraBirimi, piyasa]);

    // ── İşlemler menüsü dışına tıklayınca kapat ──────────────────────────────
    useEffect(() => {
        const handler = (e) => {
            if (islemMenuRef.current && !islemMenuRef.current.contains(e.target))
                setIslemMenuAcik(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const getToken = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        return session?.access_token;
    };

    // ── Piyasa Rejimi yükle ───────────────────────────────────────────────────
    const rejimYukle = async (mod) => {
        setRejimYukleniyor(true);
        try {
            const token = await getToken();
            const res = await axios.post(`${API_URL}/scan/karar-motoru/piyasa-rejimi`,
                { mod }, { headers: { Authorization: `Bearer ${token}` } });
            setAnlikRejim(res.data);
        } catch { }
        finally { setRejimYukleniyor(false); }
    };

    useEffect(() => { rejimYukle(piyasa); }, []);

    // ── Durum sorgula ─────────────────────────────────────────────────────────
    const durumSorgula = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) { clearInterval(intervalRef.current); return; }
            const res = await axios.get(`${API_URL}/scan/durum`,
                { headers: { Authorization: `Bearer ${session.access_token}` } });
            setIlerleme(res.data.ilerleme || 0);
            setTahminiSure(res.data.tahmini_sure);

            // Mesaj formatı: "N|i/toplam|sembol" — N = bulunan sayısı
            const mesaj = res.data.mesaj || "";
            if (mesaj.includes("|")) {
                const parcalar = mesaj.split("|");
                const bulunan = parseInt(parcalar[0], 10);
                if (!isNaN(bulunan)) setBulunanSayisi(bulunan);
                // "i/toplam" kısmından tamamlanan/toplam sayılarını çıkar
                const oranEslesme = (parcalar[1] || "").match(/(\d+)\s*\/\s*(\d+)/);
                if (oranEslesme) {
                    setTamamlananSayisi(parseInt(oranEslesme[1], 10));
                    setToplamSayisi(parseInt(oranEslesme[2], 10));
                }
                // "i/toplam Taranıyor: sembol" formatı
                const gorunenMesaj = parcalar[2]
                    ? `${parcalar[1]} Taranıyor: ${parcalar[2]}`
                    : (parcalar[1] || mesaj);
                setDurumMesaj(gorunenMesaj);
            } else {
                setDurumMesaj(mesaj);
            }

            if (!res.data.calisiyor) {
                clearInterval(intervalRef.current);
                clearInterval(gecenSureRef.current);
                setCalisiyor(false);
                if (res.data.sonuc) {
                    setBulunanSayisi(res.data.sonuc.toplam || 0);
                    setSonuc(res.data.sonuc);
                    setSecilenler(new Set());
                    if (res.data.sonuc?.rejim) setAnlikRejim(res.data.sonuc.rejim);
                    toast.success(`✅ Tamamlandı! ${res.data.sonuc.toplam || 0} fırsat bulundu`);
                } else if (res.data.hata) {
                    toast.error("Hata: " + res.data.hata);
                }
            }
        } catch { }
    };

    useEffect(() => () => {
        clearInterval(intervalRef.current);
        clearInterval(gecenSureRef.current);
    }, []);

    // ── Tarama Başlat ─────────────────────────────────────────────────────────
    const taramaBaslat = async () => {
        setSonuc(null);
        setSecilenler(new Set());
        setDetayAcik(null);
        setCalisiyor(true);
        setIlerleme(0);
        setTahminiSure(null);
        setDurumMesaj(null);
        setBulunanSayisi(0);
        setTamamlananSayisi(0);
        setToplamSayisi(0);
        setGecenSure(0);
        const simdi = Date.now();
        // Geçen süreyi her saniye güncelle
        clearInterval(gecenSureRef.current);
        gecenSureRef.current = setInterval(() => {
            setGecenSure(Math.floor((Date.now() - simdi) / 1000));
        }, 1000);
        try {
            const token = await getToken();
            await axios.post(`${API_URL}/scan/karar-motoru`, {
                mod:          piyasa,
                vade,
                // FIX #3: BIST için seçilen paraBirimi, Kripto için USD
                para_birimi:  paraBirimi,
                min_skor:     minSkor,   // FIX #4: min_skor filtre seçimden gelsin
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Karar Motoru başlatıldı!");
            intervalRef.current = setInterval(durumSorgula, 3000);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Hata oluştu");
            setCalisiyor(false);
            clearInterval(gecenSureRef.current);
        }
    };

    // ── Sıralama ──────────────────────────────────────────────────────────────
    const siralamaToggle = (kol) => {
        setSiralama(s => ({ kol, yon: s.kol === kol && s.yon === "desc" ? "asc" : "desc" }));
    };
    const siralamaOk = (kol) => siralama.kol === kol ? (siralama.yon === "desc" ? " ↓" : " ↑") : "";

    const filtreliSonuclar = (sonuc?.sonuclar || []).filter(r => {
        // Yön filtresi
        if (filtreYon !== "Tümü" && r.yon !== filtreYon) return false;
        // Sınıf filtresi (A+, A, B, C, D)
        if (filtreSinif !== "Tümü" && !r.sinif?.startsWith(filtreSinif)) return false;
        // Karar filtresi
        if (filtreKarar !== "Tümü") {
            const k = r.karar || "";
            // "AL" filtresi: hem AL hem GÜÇLÜ AL kapsar ama SAT içermemeli
            if (filtreKarar === "AL")  return k.includes("AL") && !k.includes("SAT");
            // "SAT" filtresi: hem SAT hem GÜÇLÜ SAT kapsar
            if (filtreKarar === "SAT") return k.includes("SAT");
            if (filtreKarar === "İZLE")    return k.includes("İZLE");
            if (filtreKarar === "DİKKAT") return k.includes("DİKKAT");
        }
        return true;
    });

    const siraliSonuclar = [...filtreliSonuclar].sort((a, b) => {
        // Kazanç kolonları hesaplanmış değerler
        const getVal = (r, kol) => {
            if (kol === "kazanc1") return r.fiyat && r.hedef1 ? parseFloat(((r.hedef1 - r.fiyat) / r.fiyat * 100).toFixed(1)) : null;
            if (kol === "kazanc2") return r.fiyat && r.hedef2 ? parseFloat(((r.hedef2 - r.fiyat) / r.fiyat * 100).toFixed(1)) : null;
            return r[kol] ?? null;
        };
        const av = getVal(a, siralama.kol);
        const bv = getVal(b, siralama.kol);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        if (typeof av === "number" && typeof bv === "number")
            return siralama.yon === "desc" ? bv - av : av - bv;
        return siralama.yon === "desc"
            ? String(bv).localeCompare(String(av), "tr")
            : String(av).localeCompare(String(bv), "tr");
    });

    // ── Checkbox yardımcıları ─────────────────────────────────────────────────
    const tumSecili = siraliSonuclar.length > 0 && siraliSonuclar.every(r => secilenler.has(r.sembol));
    const toggleTum = () => {
        if (tumSecili) setSecilenler(new Set());
        else setSecilenler(new Set(siraliSonuclar.map(r => r.sembol)));
    };
    const toggleSec = (sembol) => {
        setSecilenler(prev => {
            const s = new Set(prev);
            s.has(sembol) ? s.delete(sembol) : s.add(sembol);
            return s;
        });
    };

    // ── Doğruluk Analizi kaydet ──────────────────────────────────────────────
    const dogrulukKaydet = async () => {
        if (secilenler.size === 0) { toast.error("Önce sembol seçin"); return; }
        setIslemMenuAcik(false);
        setDogrulukKaydediliyor(true);
        setDogrulukPopup("yukleniyor");
        setDogrulukSonuc(null);
        await new Promise(r => setTimeout(r, 50));
        try {
            const token = await getToken();
            const seciliHisseler = siraliSonuclar.filter(r => secilenler.has(r.sembol));
            const res = await axios.post(`${API_URL}/scan/karar-motoru-analiz/kaydet`, {
                hisseler: seciliHisseler,
                mod: piyasa,
                para_birimi: pb,
                vade: vade,
            }, { headers: { Authorization: `Bearer ${token}` } });
            setDogrulukSonuc(res.data);
            setDogrulukPopup("tamam");
        } catch (e) {
            toast.error("Kaydedilemedi: " + (e.response?.data?.detail || e.message));
            setDogrulukPopup(null);
        } finally {
            setDogrulukKaydediliyor(false);
        }
    };

    // ── Alarm kaydet ──────────────────────────────────────────────────────────
    const alarmKaydet = async () => {
        const eksik = alarmHisseler.find(h => !hedefFiyatlar[h.sembol] || isNaN(parseFloat(hedefFiyatlar[h.sembol])));
        if (eksik) { toast.error(`${eksik.sembol} için hedef fiyat girin`); return; }
        setAlarmKaydediliyor(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            for (const h of alarmHisseler) {
                const fiyat = parseFloat(h.fiyat);
                const hedef = parseFloat(hedefFiyatlar[h.sembol]);
                const potansiyel = fiyat > 0 ? ((hedef - fiyat) / fiyat * 100).toFixed(2) : null;
                const { error } = await supabase.from("alerts").insert({
                    piyasa: piyasa,
                    sembol: h.sembol,
                    para_birimi: paraBirimi,
                    fiyat, hedef_fiyat: hedef,
                    potansiyel: potansiyel ? parseFloat(potansiyel) : null,
                    bildirim_mail: alarmMail, bildirim_popup: alarmBildirim,
                    user_id: session.user.id,
                });
                if (error) throw error;
            }
            toast.success(`${alarmHisseler.length} hisse için alarm eklendi!`);
            setAlarmPopup(false); setAlarmHisseler([]); setHedefFiyatlar({});
        } catch (e) {
            toast.error("Alarm kaydedilemedi: " + e.message);
        } finally { setAlarmKaydediliyor(false); }
    };

    const gecmisSimOnay = () => {
        const hisseler = gecmisDetayHisse
            ? [gecmisDetayHisse.sembol]
            : Array.from(secilenler);
        navigate("/gecmis-simulasyon", {
            state: {
                kaynak:        "karar_motoru",
                kaynak_label:  "Karar Motoru",
                mod:           piyasa === "Kripto" ? "KRIPTO" : "BIST",
                para_birimi:   pb,
                sim_tarihi:    gecmisSimTarihi,
                ref_fiyat:     gecmisRefFiyat,
                hisse_listesi: hisseler,
                oto_baslat:    true,
                vade,
                min_puan:      gecmisMinSkor,
            }
        });
        setGecmisPopupAdim(null);
        setGecmisDetayHisse(null);
    };

    // ── Excel İndir ───────────────────────────────────────────────────────────
    const karaMotoruVerisi = () => {
        if (!sonuc?.sonuclar?.length) return [];
        const pb = paraBirimi === "USD (Futures)" ? "USD-F" : paraBirimi;
        return sonuc.sonuclar.map(r => ({
            "Sembol": r.sembol,
            [`Fiyat (${pb})`]: r.fiyat,
            "Yön": r.yon === "short" ? "📉 Short" : "📈 Long",
            "Giriş Skoru": r.giris_skoru,
            "Sınıf": r.sinif,
            "Karar": r.karar,
            "Strateji": r.strateji,
            [`Stop Loss (${pb})`]: r.stop_loss,
            [`Hedef 1 (${pb})`]: r.hedef1,
            "Kazanç 1 (%)": yuzde(r.fiyat, r.hedef1),
            [`Hedef 2 (${pb})`]: r.hedef2,
            "Kazanç 2 (%)": yuzde(r.fiyat, r.hedef2),
            "R/Ö H1": r.rr1,
            "R/Ö H2": r.rr2,
            "RSI": r.rsi,
            "EMA20": r.ema20,
            "EMA50": r.ema50,
            "Trend Puan": r.detay?.trend?.puan,
            "Hacim Puan": r.detay?.hacim?.puan,
            "Momentum Puan": r.detay?.momentum?.puan,
            "Formasyon": r.detay?.formasyon?.ad,
            "Smart Money": r.detay?.smart_money?.detay,
            "Vade": r.vade,
        }));
    };

    const pb = piyasa === "BIST" ? paraBirimi : paraBirimi === "USD (Futures)" ? "USD-F" : "USD";  // aktif para birimi

    // ─── RENDER ──────────────────────────────────────────────────────────────

    return (
        <div style={styles.sayfa}>
            <h2 style={styles.baslik}>🧭 Yatırım Karar Motoru</h2>
            <p style={styles.altBaslik}>
                Tek bir sembol için "AL mı, SAT mı, İZLE mi?" sorusuna tüm modülleri (Trend, Hacim, Momentum, Formasyon,
                Smart Money, Destek/Direnç, Sektör Gücü) tek bir birleşik skor ve net bir karar cümlesiyle yanıtlayan
                üst-seviye tarama. Master Skor = Giriş Kalitesi × 0.6 + Piyasa Rejimi × 0.4 formülüyle hesaplanır;
                piyasanın genel rejimine (Boğa/Ayı/Yatay) aykırı sinyaller otomatik olarak cezalandırılır.
            </p>

            {/* ── Kontrol Paneli ── */}
            <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:12,
                padding:"16px 20px",marginBottom:20,display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-end",
                opacity:calisiyor?0.5:1,pointerEvents:calisiyor?"none":"auto",transition:"opacity 0.3s"}}>

                {/* Piyasa */}
                <fieldset style={{border:"1px solid #334155",borderRadius:8,padding:"6px 12px 10px",margin:0}}>
                    <legend style={{fontSize:11,color:"#94a3b8",fontWeight:600,padding:"0 4px",letterSpacing:"0.05em"}}>PİYASA</legend>
                    <div style={{display:"flex",gap:6}}>
                        {piyasaSecenekleri.map(p=>(
                            <button key={p} onClick={()=>setPiyasa(p)} style={{
                                padding:"5px 14px",borderRadius:6,fontWeight:600,fontSize:13,cursor:"pointer",
                                border:`2px solid ${piyasa===p?"#3b82f6":"#334155"}`,
                                background:piyasa===p?"#3b82f622":"transparent",
                                color:piyasa===p?"#3b82f6":"#94a3b8"}}>{p}</button>
                        ))}
                    </div>
                </fieldset>

                {/* Para Birimi */}
                <fieldset style={{border:"1px solid #334155",borderRadius:8,padding:"6px 12px 10px",margin:0}}>
                    <legend style={{fontSize:11,color:"#94a3b8",fontWeight:600,padding:"0 4px",letterSpacing:"0.05em"}}>PARA BİRİMİ</legend>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {paraBirimiSecenekleri.map(p=>{
                            const kilitli = paraBirimiSecenekleri.length <= 1;
                            return (
                            <button key={p} disabled={kilitli} onClick={()=>setParaBirimi(p)} style={{
                                padding:"5px 12px",borderRadius:6,fontWeight:600,fontSize:12,
                                cursor:kilitli?"default":"pointer",
                                border:`2px solid ${paraBirimi===p?"#a78bfa":"#334155"}`,
                                background:paraBirimi===p?"#a78bfa22":"transparent",
                                opacity:kilitli&&paraBirimi!==p?0.4:1,
                                color:paraBirimi===p?"#a78bfa":"#94a3b8"}}>{p}</button>
                            );
                        })}
                    </div>
                </fieldset>

                {/* Vade */}
                <fieldset style={{border:"1px solid #334155",borderRadius:8,padding:"6px 12px 10px",margin:0}}>
                    <legend style={{fontSize:11,color:"#94a3b8",fontWeight:600,padding:"0 4px",letterSpacing:"0.05em"}}>VADE</legend>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {(VADE_OPTS[piyasa] || []).map(v=>{
                            const isFutures = piyasa === "Kripto" && paraBirimi === "USD (Futures)";
                            const isSpot    = piyasa === "Kripto" && paraBirimi !== "USD (Futures)";
                            const disabled  = (isFutures && v !== "Kaldıraçlı İşlem")
                                           || (isSpot    && v === "Kaldıraçlı İşlem");
                            return (
                            <button key={v} disabled={disabled} onClick={()=>{ if(!disabled) setVade(v); }} style={{
                                padding:"5px 12px",borderRadius:6,fontWeight:600,fontSize:12,
                                cursor:disabled?"default":"pointer",
                                border:`2px solid ${vade===v?"#eab308":"#334155"}`,
                                background:vade===v?"#eab30822":"transparent",
                                opacity:disabled?0.35:1,
                                color:vade===v?"#eab308":"#94a3b8"}}>{v}</button>
                            );
                        })}
                    </div>
                </fieldset>

                {/* Minimum Skor */}
                <fieldset style={{border:"1px solid #334155",borderRadius:8,padding:"6px 12px 10px",margin:0}}>
                    <legend style={{fontSize:11,color:"#94a3b8",fontWeight:600,padding:"0 4px",letterSpacing:"0.05em"}}>MİNİMUM SKOR</legend>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {MIN_SKOR_OPTS.map(s=>(
                            <button key={s} onClick={()=>setMinSkor(s)} style={{
                                padding:"5px 10px",borderRadius:6,fontWeight:600,fontSize:12,cursor:"pointer",
                                border:`2px solid ${minSkor===s?"#059669":"#334155"}`,
                                background:minSkor===s?"#05966922":"transparent",
                                color:minSkor===s?"#059669":"#94a3b8"}}>{s===0?"Tümü":`${s}+`}</button>
                        ))}
                    </div>
                </fieldset>

                {/* Başlat */}
                <button onClick={taramaBaslat} style={{
                    padding:"10px 28px",borderRadius:8,fontWeight:700,fontSize:14,cursor:"pointer",
                    background:"linear-gradient(135deg,#4c1d95,#7c3aed)",
                    border:"none",color:"white",alignSelf:"flex-end"}}>
                    🔍 Tara
                </button>
            </div>

            {/* Uyarı + İlerleme */}
            {calisiyor && (
                <>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,
                        padding:"10px 16px",borderRadius:8,background:"#422006",border:"1px solid #92400e"}}>
                        <span style={{fontSize:16}}>⚠️</span>
                        <span style={{fontSize:13,color:"#fde68a",fontWeight:600}}>
                            Tarama tamamlanıncaya kadar sayfayı kapatmayın
                        </span>
                    </div>
                    <TaramaIlerlemePaneli
                        ilerleme={ilerleme}
                        tamamlanan={tamamlananSayisi}
                        toplam={toplamSayisi}
                        bulunan={bulunanSayisi}
                        gecen={gecenSure}
                        tahminiSure={tahminiSure}
                    />
                </>
            )}

            {/* Yan Yana: Piyasa Rejimi (sol) + Sağ Kolon (sağ) */}
            <div style={{ display: "flex", gap: "14px", marginBottom: "14px", alignItems: "stretch" }}>

                {/* Sol — Piyasa Rejimi Kartı */}
                <div style={{ flex: "0 0 65%", minWidth: 0 }}>
                    {rejimYukleniyor
                        ? <div style={{ background: "#1e293b", borderRadius: "12px", padding: "14px",
                            color: "#94a3b8", fontSize: "13px", border: "1px solid #334155", height: "100%" }}>
                            ⏳ Piyasa rejimi analiz ediliyor...
                          </div>
                        : <PiyasaRejimiKart
                            rejim={
                                (sonuc?.rejim && sonuc?.mod === piyasa)
                                    ? sonuc.rejim
                                    : anlikRejim
                            }
                          />
                    }
                </div>

                {/* Sağ — Vade Kartı + Skor Kartı */}
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                    {/* Vade / Zaman Dilimi */}
                    <ZamanDilimiKart vade={vade} />

                    {/* Skor Referans Kartı */}
                    <div style={{ background: "#1e293b", borderRadius: "10px",
                        padding: "10px 14px", border: "1px solid #334155" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#93c5fd",
                            marginBottom: "8px" }}>🎯 Skor Rehberi</div>
                        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                            {[
                                { label: "90+ → A+",  bg: "#16a34a22", c: "#15803d" },
                                { label: "80-89 → A", bg: "#05966922", c: "#059669" },
                                { label: "70-79 → B", bg: "#2563eb22", c: "#2563eb" },
                                { label: "60-69 → C", bg: "#f59e0b22", c: "#d97706" },
                                { label: "<60 → ✗",   bg: "#dc262622", c: "#dc2626" },
                            ].map(item => (
                                <span key={item.label} style={{ padding: "3px 9px",
                                    borderRadius: "20px", fontSize: "11px", fontWeight: 700,
                                    background: item.bg, color: item.c, whiteSpace: "nowrap" }}>
                                    {item.label}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Uyarı */}
            {sonuc?.uyari && (
                <div style={{ background: "#f59e0b22", borderRadius: "9px", padding: "12px 16px",
                    color: "#92400e", fontSize: "13px", fontWeight: 600,
                    marginBottom: "14px", border: "1px solid #92400e" }}>
                    {sonuc.uyari}
                </div>
            )}

            {/* ── Sonuç bilgisi + İşlemler ── */}
            {sonuc && (
                <div style={styles.bilgi}>
                    {/* İşlemler menüsü — sol */}
                    <div ref={islemMenuRef} style={{ position: "relative" }}>
                        <button onClick={() => setIslemMenuAcik(v => !v)}
                            style={{ padding: "6px 16px", borderRadius: "8px", border: "1px solid #334155",
                                background: "#1e293b", cursor: "pointer", fontSize: "13px", fontWeight: 500,
                                color: "#e2e8f0", display: "flex", alignItems: "center", gap: 5 }}>
                            İşlemler ▾
                        </button>
                        {islemMenuAcik && (
                            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0,
                                background: "#1e293b", border: "1px solid #334155", borderRadius: "10px",
                                boxShadow: "0 8px 24px rgba(0,0,0,0.4)", zIndex: 100, minWidth: "260px", overflow: "visible" }}>
                                {[
                                    {
                                        label: dogrulukKaydediliyor ? "⏳ Kaydediliyor..." : "✅ Doğruluk Analizine Gönder",
                                        onClick: dogrulukKaydet,
                                        disabled: dogrulukKaydediliyor,
                                    },
                                    {
                                        label: "🔮 Geçmiş Simülasyonuna Gönder",
                                        onClick: () => {
                                            if (secilenler.size === 0) { toast.error("Önce sembol seçin"); return; }
                                            setIslemMenuAcik(false);
                                            setGecmisPopupAdim("kriter");
                                        }
                                    },
                                    {
                                        label: "🔗 Çoklu Taramaya Gönder",
                                        onClick: () => {
                                            if (secilenler.size === 0) { toast.error("Önce sembol seçin"); return; }
                                            setIslemMenuAcik(false);
                                            setCokluSecModul(null); setCokluFiltre(null); setCokluPeriyotlar([]);
                                            setCokluPopup(true);
                                        }
                                    },
                                    {
                                        label: "⭐ Puanlamaya Gönder",
                                        onClick: () => {
                                            if (secilenler.size === 0) { toast.error("Önce sembol seçin"); return; }
                                            setIslemMenuAcik(false);
                                            setPuanlamaVade(vade);
                                            setPuanlamaMinPuan(0);
                                            setPuanlamaPopupAdim("kriter");
                                        }
                                    },
                                    {
                                        label: "⏰ Alarm Ekle",
                                        onClick: () => {
                                            if (secilenler.size === 0) { toast.error("Önce sembol seçin"); return; }
                                            const seciliHisseler = siraliSonuclar
                                                .filter(r => secilenler.has(r.sembol))
                                                .map(r => ({ sembol: r.sembol, fiyat: r.fiyat }));
                                            setAlarmHisseler(seciliHisseler);
                                            setHedefFiyatlar({});
                                            setAlarmMail(false);
                                            setAlarmBildirim(true);
                                            setAlarmPopup(true);
                                            setIslemMenuAcik(false);
                                        }
                                    },
                                    { label: "🔎 İzleme Listesine Ekle", onClick: () => {}, disabled: true },
                                ].map(item => (
                                    <button key={item.label}
                                        onClick={item.disabled ? undefined : item.onClick}
                                        disabled={!!item.disabled}
                                        style={{ display: "block", width: "100%", textAlign: "left",
                                            padding: "10px 16px", border: "none", background: "none",
                                            cursor: item.disabled ? "default" : "pointer",
                                            color: item.disabled ? "#94a3b8" : "#e2e8f0", fontSize: "13px",
                                            borderBottom: "1px solid #334155" }}>
                                        {item.label}
                                    </button>
                                ))}
                                <IndirMenusu veriler={karaMotoruVerisi} setIslemMenuAcik={setIslemMenuAcik} />
                            </div>
                        )}
                    </div>

                    <div style={{width:1,height:20,background:"#334155"}}/>
                    <span>🧭 <strong>{sonuc.strateji || "—"}</strong></span>
                    <span>⏳ <strong>{vade}</strong></span>
                    {/* FIX #3: Para birimi sonuçtan değil, seçimden */}
                    <span>💱 <strong>{pb}</strong></span>
                    <span>✨ <strong>{sonuc.toplam}</strong> fırsat ({secilenler.size} seçili)</span>

                    {/* Skor dağılımı */}
                    <div style={{ display: "flex", gap: "5px", marginLeft: "4px" }}>
                        {[
                            { l: "A (80+)", f: r => r.giris_skoru >= 80, c: "#15803d", bg: "#16a34a22" },
                            { l: "B (70+)", f: r => r.giris_skoru >= 70 && r.giris_skoru < 80, c: "#2563eb", bg: "#2563eb22" },
                            { l: "C (60+)", f: r => r.giris_skoru >= 60 && r.giris_skoru < 70, c: "#d97706", bg: "#f59e0b22" },
                        ].map(item => {
                            const cnt = sonuc.sonuclar.filter(item.f).length;
                            if (cnt === 0) return null;
                            return (
                                <span key={item.l} style={{ fontSize: "11px", padding: "2px 8px",
                                    borderRadius: "20px", background: item.bg, color: item.c, fontWeight: 700 }}>
                                    {item.l}: {cnt}
                                </span>
                            );
                        })}
                    </div>

                    <span style={{ color: "#94a3b8", fontSize: "12px", marginLeft: "auto" }}>
                        {new Date(sonuc.tarih).toLocaleString("tr-TR")}
                    </span>
                </div>
            )}

            {/* ── Filtre Kartı: Yön / Sınıf / Karar (sonuç tablosundan hemen önce) ── */}
            {sonuc && (
                <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "12px", padding: "12px 20px",
                    marginBottom: "12px", display: "flex", gap: "20px", alignItems: "center", flexWrap: "wrap" }}>

                    {/* Yön filtresi */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.05em" }}>YÖN</span>
                        {[
                            { v: "Tümü", l: "Tümü", renk: "#94a3b8" },
                            { v: "long", l: "📈 Long", renk: "#16a34a" },
                            { v: "short", l: "📉 Short", renk: "#dc2626" },
                        ].map(opt => {
                            const aktif = filtreYon === opt.v;
                            return (
                                <button key={opt.v} onClick={() => setFiltreYon(opt.v)} style={{
                                    padding: "4px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                                    fontWeight: aktif ? 700 : 400,
                                    border: `2px solid ${aktif ? opt.renk : "#334155"}`,
                                    background: aktif ? opt.renk + "22" : "transparent",
                                    color: aktif ? opt.renk : "#94a3b8" }}>{opt.l}</button>
                            );
                        })}
                    </div>

                    <div style={{width:1,height:20,background:"#334155"}}/>

                    {/* Sınıf filtresi */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.05em" }}>SINIF</span>
                        {["Tümü", "A+", "A", "B", "C", "D"].map(v => {
                            const aktif = filtreSinif === v;
                            return (
                                <button key={v} onClick={() => setFiltreSinif(v)} style={{
                                    padding: "4px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                                    fontWeight: aktif ? 700 : 400,
                                    border: `2px solid ${aktif ? "#3b82f6" : "#334155"}`,
                                    background: aktif ? "#3b82f622" : "transparent",
                                    color: aktif ? "#3b82f6" : "#94a3b8" }}>{v}</button>
                            );
                        })}
                    </div>

                    <div style={{width:1,height:20,background:"#334155"}}/>

                    {/* Karar filtresi */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.05em" }}>KARAR</span>
                        {["Tümü", "AL", "SAT", "İZLE"].map(v => {
                            const renk = v === "AL" ? "#16a34a" : v === "SAT" ? "#dc2626" : v === "İZLE" ? "#d97706" : "#94a3b8";
                            const aktif = filtreKarar === v;
                            return (
                                <button key={v} onClick={() => setFiltreKarar(v)} style={{
                                    padding: "4px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                                    fontWeight: aktif ? 700 : 400,
                                    border: `2px solid ${aktif ? renk : "#334155"}`,
                                    background: aktif ? renk + "22" : "transparent",
                                    color: aktif ? renk : "#94a3b8" }}>{v}</button>
                            );
                        })}
                    </div>

                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 12, color: "#94a3b8" }}>
                            <strong style={{ color: "#e2e8f0" }}>{siraliSonuclar.length}</strong> / {sonuc.sonuclar?.length || 0} sonuç
                        </span>
                        {(filtreYon !== "Tümü" || filtreSinif !== "Tümü" || filtreKarar !== "Tümü") && (
                            <button onClick={() => { setFiltreYon("Tümü"); setFiltreSinif("Tümü"); setFiltreKarar("Tümü"); }}
                                style={{ padding: "4px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                                    border: "1px solid #dc262666", background: "#dc262611", color: "#dc2626" }}>
                                ✕ Temizle
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* ── Tablo ── */}
            {siraliSonuclar.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                    <table style={styles.tablo}>
                        <thead>
                            <tr style={styles.thead}>
                                {/* FIX #5: # yerine checkbox */}
                                <th style={{ ...styles.th, width: "36px", textAlign: "center" }}>
                                    <input type="checkbox" checked={tumSecili} onChange={toggleTum}
                                        style={{ cursor: "pointer", width: "15px", height: "15px" }} />
                                </th>
                                {[
                                    { l: "Sembol",          k: "sembol" },
                                    { l: `Fiyat (${pb})`,  k: "fiyat" },
                                    { l: "Yön",             k: "yon" },
                                    { l: "Skor",            k: "giris_skoru", bilgi: "Skor" },
                                    { l: "Sınıf",           k: "sinif", bilgi: "Sınıf" },
                                    { l: "Karar",           k: "karar", bilgi: "Karar" },
                                    { l: `Stop (${pb})`,   k: "stop_loss", bilgi: "Stop" },
                                    { l: `Hedef 1 (${pb})`,k: "hedef1", bilgi: "Hedef1" },
                                    { l: "Kazanç 1",        k: "kazanc1", bilgi: "Kazanç1" },
                                    { l: `Hedef 2 (${pb})`,k: "hedef2", bilgi: "Hedef2" },
                                    { l: "Kazanç 2",        k: "kazanc2", bilgi: "Kazanç2" },
                                    { l: "RSI",             k: "rsi", bilgi: "RSI" },
                                ].map(col => (
                                    <th key={col.l}
                                        onClick={() => col.k && siralamaToggle(col.k)}
                                        style={{ ...styles.th, cursor: col.k ? "pointer" : "default",
                                            userSelect: "none",
                                            background: siralama.kol === col.k ? "#163160" : undefined }}>
                                        {col.l}{col.bilgi && <BilgiIkonu alan={col.bilgi}/>}{col.k ? siralamaOk(col.k) : ""}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {siraliSonuclar.map((r, i) => {
                                const secili = secilenler.has(r.sembol);
                                const k1 = yuzde(r.fiyat, r.hedef1);
                                const k2 = yuzde(r.fiyat, r.hedef2);
                                return (
                                    <tr key={r.sembol}
                                        onClick={() => setDetayAcik(detayAcik?.sembol === r.sembol ? null : r)}
                                        style={{ background: secili ? "#3b82f622" : i % 2 === 0 ? "#1e293b" : "#182234",
                                            cursor: "pointer" }}>

                                        {/* Checkbox */}
                                        <td style={{ ...styles.td, textAlign: "center" }}
                                            onClick={e => { e.stopPropagation(); toggleSec(r.sembol); }}>
                                            <input type="checkbox" checked={secili}
                                                onChange={() => {}}
                                                style={{ cursor: "pointer", width: "15px", height: "15px",
                                                    pointerEvents: "none" }} />
                                        </td>

                                        <td style={{ ...styles.td, fontWeight: 800, color: "#93c5fd", fontSize: "14px" }}>
                                            {r.sembol}
                                        </td>
                                        <td style={{ ...styles.td, fontWeight: 600 }}>{r.fiyat}</td>

                                        {/* Yön */}
                                        <td style={{ ...styles.td, textAlign: "center" }}>
                                            {r.yon === "short"
                                                ? <span style={{ fontSize: "12px", fontWeight: 700, color: "#dc2626" }}>📉 Short</span>
                                                : <span style={{ fontSize: "12px", fontWeight: 700, color: "#16a34a" }}>📈 Long</span>
                                            }
                                        </td>

                                        {/* Skor */}
                                        <td style={styles.td}>
                                            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                width: "40px", height: "26px", borderRadius: "7px",
                                                background: skorBg(r.giris_skoru, r.yon), color: skorRenk(r.giris_skoru, r.yon),
                                                fontWeight: 800, fontSize: "13px" }}>
                                                {r.giris_skoru}
                                            </span>
                                        </td>

                                        {/* Sınıf */}
                                        <td style={styles.td}>
                                            <span style={{ ...sinifRenk(r.sinif), padding: "2px 9px",
                                                borderRadius: "20px", fontSize: "11px", fontWeight: 700,
                                                whiteSpace: "nowrap" }}>
                                                {r.sinif}
                                            </span>
                                        </td>

                                        {/* Karar */}
                                        <td style={{ ...styles.td, color: kararRenk(r.karar),
                                            fontWeight: 700, fontSize: "12px", whiteSpace: "nowrap" }}>
                                            {r.karar}
                                        </td>


                                        <td style={{ ...styles.td, color: "#dc2626", fontWeight: 600 }}>
                                            {r.stop_loss ?? "—"}
                                        </td>

                                        {/* Hedef 1 + Kazanç 1 */}
                                        <td style={{ ...styles.td,
                                            color: r.yon === "short" ? "#dc2626" : "#16a34a",
                                            fontWeight: 600 }}>
                                            {r.hedef1 ?? "—"}
                                        </td>
                                        <td style={styles.td}>
                                            {k1 !== null ? (
                                                <span style={{ fontSize: "12px", fontWeight: 700,
                                                    color: r.yon === "short"
                                                        ? (parseFloat(k1) <= 0 ? "#dc2626" : "#94a3b8")
                                                        : (parseFloat(k1) >= 0 ? "#16a34a" : "#dc2626") }}>
                                                    {parseFloat(k1) >= 0 ? "+" : ""}{k1}%
                                                </span>
                                            ) : "—"}
                                        </td>

                                        {/* Hedef 2 + Kazanç 2 */}
                                        <td style={{ ...styles.td,
                                            color: r.yon === "short" ? "#991b1b" : "#059669",
                                            fontWeight: 600 }}>
                                            {r.hedef2 ?? "—"}
                                        </td>
                                        <td style={styles.td}>
                                            {k2 !== null ? (
                                                <span style={{ fontSize: "12px", fontWeight: 700,
                                                    color: r.yon === "short"
                                                        ? (parseFloat(k2) <= 0 ? "#991b1b" : "#94a3b8")
                                                        : (parseFloat(k2) >= 0 ? "#059669" : "#dc2626") }}>
                                                    {parseFloat(k2) >= 0 ? "+" : ""}{k2}%
                                                </span>
                                            ) : "—"}
                                        </td>

                                        {/* RSI */}
                                        <td style={styles.td}>
                                            {r.rsi ? (
                                                <span style={{ padding: "2px 6px", borderRadius: "5px",
                                                    background: r.rsi > 70 ? "#dc262622" : r.rsi < 30 ? "#16a34a22" : "#334155",
                                                    color: r.rsi > 70 ? "#dc2626" : r.rsi < 30 ? "#16a34a" : "#e2e8f0",
                                                    fontSize: "12px", fontWeight: 600 }}>
                                                    {r.rsi}
                                                </span>
                                            ) : "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {sonuc?.sonuclar?.length === 0 && (
                <div style={{ textAlign: "center", padding: "3rem", color: "#94a3b8" }}>
                    ⚠️ Seçilen kriterlere uygun sonuç bulunamadı. Min skoru düşürmeyi deneyin.
                </div>
            )}

            {/* Metodoloji notu */}
            {!sonuc && !calisiyor && (
                <div style={{ marginTop: "20px", padding: "14px 18px", background: "#182234",
                    borderRadius: "10px", border: "1px solid #334155", fontSize: "12px", color: "#94a3b8" }}>
                    <strong style={{ color: "#e2e8f0" }}>📚 Metodoloji:</strong>{" "}
                    Trend(%20) + Hacim(%20) + Momentum RSI+MACD(%15) + Formasyon(%15) + Smart Money FVG+Likidite+OB(%20) + Destek/Direnç(%10).
                    Master Skor = Giriş Kalitesi × 0.6 + Piyasa Rejimi × 0.4.
                </div>
            )}

            {/* ── Detay Panel (sağdan overlay) ── */}
            {detayAcik && (
                <>
                    <div onClick={() => setDetayAcik(null)}
                        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 999 }} />
                    <DetayPanel r={detayAcik} onKapat={() => setDetayAcik(null)} onSimulasyon={(r) => { setDetayAcik(null); setGecmisDetayHisse(r); setGecmisPopupAdim("kriter"); }} />
                </>
            )}

            {/* ── Alarm Popup ── */}
            {/* Doğruluk Popup */}
            {dogrulukPopup && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",
                    display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000}}>
                    <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:14,
                        padding:"2rem",width:420,maxWidth:"95vw",boxShadow:"0 12px 48px rgba(0,0,0,0.5)"}}>
                        {dogrulukPopup==="yukleniyor"?(
                            <>
                                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
                                    <span style={{fontSize:24}}>✅</span>
                                    <div>
                                        <div style={{fontSize:16,fontWeight:700,color:"#e2e8f0"}}>Doğruluk Analizi</div>
                                        <div style={{fontSize:12,color:"#94a3b8",marginTop:2}}>Veriler kaydediliyor...</div>
                                    </div>
                                </div>
                                <HareketliBar/>
                                <div style={{fontSize:13,color:"#94a3b8",textAlign:"center"}}>Kaydediliyor...</div>
                            </>
                        ):(
                            <>
                                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
                                    <span style={{fontSize:28}}>✅</span>
                                    <div style={{fontSize:16,fontWeight:700,color:"#e2e8f0"}}>Kayıt Tamamlandı</div>
                                </div>
                                <div style={{background:"#16a34a22",border:"1px solid #16a34a44",
                                    borderRadius:8,padding:"14px 16px",marginBottom:20,fontSize:14,color:"#16a34a",lineHeight:1.6}}>
                                    Seçilen semboller <strong>"Doğruluk Analizi"</strong> veri tabanına kaydedildi.
                                    {dogrulukSonuc && (
                                        <div style={{marginTop:8,fontSize:12,color:"#94a3b8"}}>
                                            ✔ {dogrulukSonuc.eklenen} yeni · {dogrulukSonuc.atlanan} mevcut
                                        </div>
                                    )}
                                </div>
                                <div style={{display:"flex",justifyContent:"flex-end"}}>
                                    <button onClick={()=>{setDogrulukPopup(null);setDogrulukSonuc(null);}}
                                        style={{padding:"8px 28px",borderRadius:8,border:"none",
                                            background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",
                                            color:"white",cursor:"pointer",fontSize:14,fontWeight:600}}>
                                        Tamam
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Puanlama Kriter */}
            {puanlamaPopupAdim === "kriter" && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
                    <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:14,padding:"2rem",width:440,maxWidth:"95vw",boxShadow:"0 12px 48px rgba(0,0,0,0.5)",color:"#e2e8f0"}}>
                        <div style={{fontSize:18,fontWeight:700,color:"#e2e8f0",marginBottom:12}}>⭐ Puanlamaya Gönder</div>
                        <p style={{fontSize:13,color:"#94a3b8",marginBottom:"1.25rem"}}>
                            <strong>{secilenler.size}</strong> sembol seçildi. Puanlama kriterlerini belirleyin.
                        </p>
                        <div style={{display:"flex",flexDirection:"column",gap:"1rem",marginBottom:"1.25rem"}}>
                            <div>
                                <label style={{fontSize:12,fontWeight:600,color:"#93c5fd",display:"block",marginBottom:6}}>Vade</label>
                                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                                    {(VADE_OPTS[piyasa] || []).map(v => (
                                        <button key={v} type="button" onClick={() => setPuanlamaVade(v)} style={{
                                            padding:"6px 12px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",
                                            border:`2px solid ${puanlamaVade===v?"#3b82f6":"#334155"}`,
                                            background:puanlamaVade===v?"#3b82f622":"transparent",
                                            color:puanlamaVade===v?"#3b82f6":"#e2e8f0"}}>{v}</button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label style={{fontSize:12,fontWeight:600,color:"#93c5fd",display:"block",marginBottom:6}}>Min Puan</label>
                                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                                    {[0,50,60,65,70,75,80].map(v => (
                                        <button key={v} type="button" onClick={() => setPuanlamaMinPuan(v)} style={{
                                            padding:"5px 10px",borderRadius:6,fontSize:12,fontWeight:600,cursor:"pointer",
                                            border:`2px solid ${puanlamaMinPuan===v?"#eab308":"#334155"}`,
                                            background:puanlamaMinPuan===v?"#eab30822":"transparent",
                                            color:puanlamaMinPuan===v?"#eab308":"#94a3b8"}}>{v===0?"Tümü":`${v}+`}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                            <button onClick={() => setPuanlamaPopupAdim(null)} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #334155",background:"transparent",cursor:"pointer",fontSize:13,color:"#94a3b8"}}>İptal</button>
                            <button onClick={() => setPuanlamaPopupAdim("onay")} style={{padding:"8px 20px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",color:"white",cursor:"pointer",fontSize:13,fontWeight:600}}>Devam Et →</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Puanlama Onay */}
            {puanlamaPopupAdim === "onay" && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
                    <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:14,padding:"2rem",width:440,maxWidth:"95vw",boxShadow:"0 12px 48px rgba(0,0,0,0.5)",color:"#e2e8f0"}}>
                        <div style={{fontSize:18,fontWeight:700,color:"#e2e8f0",marginBottom:12}}>⚠️ Sayfadan Ayrılıyorsunuz</div>
                        <div style={{background:"#422006",border:"1px solid #92400e",borderRadius:8,padding:"1rem",marginBottom:20,fontSize:13,color:"#fde68a",lineHeight:1.7}}>
                            Başka bir sayfaya yönlendiriliyorsunuz. <strong>Ekrandaki tarama sonuçları kaybolacak.</strong>
                            <br/><br/>Seçili <strong>{secilenler.size} sembol</strong> için <strong>Puanlama ve Tahmin</strong> taraması otomatik olarak başlatılacaktır. Emin misiniz?
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:20,fontSize:12}}>
                            {[["Piyasa",piyasa],["Para Birimi",pb],["Vade",puanlamaVade],
                              ["Min Puan",puanlamaMinPuan===0?"0+ (Tümü)":`${puanlamaMinPuan}+`],
                              ["Sembol Sayısı",`${secilenler.size} sembol`],
                            ].map(([k,v]) => (
                                <div key={k} style={{display:"flex",justifyContent:"space-between",background:"#0f172a",borderRadius:6,padding:"6px 10px"}}>
                                    <span style={{color:"#94a3b8"}}>{k}:</span>
                                    <strong style={{color:"#e2e8f0"}}>{v}</strong>
                                </div>
                            ))}
                        </div>
                        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                            <button onClick={() => setPuanlamaPopupAdim("kriter")} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #334155",background:"transparent",cursor:"pointer",fontSize:13,color:"#94a3b8"}}>← Geri</button>
                            <button onClick={() => setPuanlamaPopupAdim(null)} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #334155",background:"transparent",cursor:"pointer",fontSize:13,color:"#94a3b8"}}>Hayır, İptal</button>
                            <button onClick={() => {
                                const hisseListesi = Array.from(secilenler);
                                setPuanlamaPopupAdim(null);
                                navigate("/puanlama-tahmin", {
                                    state: {
                                        kaynak: "karar_motoru",
                                        piyasa, para_birimi: pb, vade: puanlamaVade, min_puan: puanlamaMinPuan,
                                        hisse_listesi: hisseListesi, oto_baslat: true,
                                    }
                                });
                            }} style={{padding:"8px 20px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",color:"white",cursor:"pointer",fontSize:13,fontWeight:600}}>Evet, Başlat 🚀</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Çoklu Taramaya Gönder */}
            {cokluPopup && (
                <CokluTaramaGonderPopup
                    piyasa={piyasa}
                    paraBirimi={pb}
                    secilenSayisi={secilenler.size}
                    secilenModul={cokluSecModul}
                    filtre={cokluFiltre}
                    periyotlar={cokluPeriyotlar}
                    onModulSec={m=>{
                        setCokluSecModul(m);setCokluFiltre(defaultCokluFiltreler(m,piyasa));
                        const dbKey=MODUL_KEY_MAP[m];
                        if(dbKey) getLookupPeriyotlar(dbKey).then(list=>setCokluPeriyotlar(list)).catch(()=>setCokluPeriyotlar([]));
                        else setCokluPeriyotlar([]);
                    }}
                    onFiltreDegis={setCokluFiltre}
                    onIptal={()=>{setCokluPopup(false);setCokluSecModul(null);setCokluFiltre(null);}}
                    onBaslat={()=>{if(!cokluSecModul||!cokluFiltre){toast.error("Modül seçin");return;}setCokluPopup(false);setCokluOnayPopup(true);}}/>
            )}

            {/* Çoklu Tarama — Onay */}
            {cokluOnayPopup && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
                    <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:14,padding:"1.75rem",width:420,maxWidth:"95vw",boxShadow:"0 12px 48px rgba(0,0,0,0.5)",color:"#e2e8f0"}}>
                        <div style={{fontSize:17,fontWeight:700,color:"#e2e8f0",marginBottom:12}}>⚠️ Sayfadan Ayrılıyorsunuz</div>
                        <div style={{background:"#422006",border:"1px solid #92400e",borderRadius:8,padding:"1rem",marginBottom:20,fontSize:13,color:"#fde68a",lineHeight:1.7}}>
                            Başka bir sayfaya yönlendiriliyorsunuz. <strong>Bu sayfadaki tarama sonuçları kaybolacak.</strong>
                            <br/><br/>Seçili <strong>{secilenler.size} sembol</strong> ile <strong>{cokluSecModul}</strong> taraması Çoklu Tarama'da otomatik başlatılacaktır.
                            <br/><br/>Devam etmek istiyor musunuz?
                        </div>
                        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                            <button onClick={()=>{setCokluOnayPopup(false);setCokluPopup(true);}} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #334155",background:"transparent",cursor:"pointer",fontSize:13,color:"#94a3b8"}}>← Geri Dön</button>
                            <button onClick={()=>{
                                const hisseListesi = Array.from(secilenler);
                                setCokluOnayPopup(false);
                                navigate("/coklu-tarama",{state:{
                                    kaynak:"karar_motoru",piyasa,paraBirimi:pb,
                                    kaynakModul:"Karar Motoru",
                                    kaynakSonucHisseler:hisseListesi,
                                    modul:cokluSecModul,filtre:cokluFiltre,hisseListesi,oto_baslat:true,
                                }});
                            }} style={{padding:"8px 20px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",color:"white",cursor:"pointer",fontSize:13,fontWeight:600}}>Evet, Devam Et →</button>
                        </div>
                    </div>
                </div>
            )}

            {gecmisPopupAdim === "kriter" && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
                    <div style={{background:"#1e293b",borderRadius:"14px",padding:"2rem",width:"420px",maxWidth:"95vw",boxShadow:"0 12px 48px rgba(0,0,0,0.25)"}}>
                        <div style={{fontSize:"18px",fontWeight:"700",color:"#93c5fd",marginBottom:"0.75rem"}}>🔮 Geçmiş Simülasyonu</div>
                        <p style={{fontSize:"13px",color:"#94a3b8",marginBottom:"1.25rem"}}>
                            <strong>{secilenler.size}</strong> sembol seçildi. Simülasyon kriterlerini belirleyin.
                        </p>
                        <div style={{display:"flex",flexDirection:"column",gap:"1rem",marginBottom:"1.25rem"}}>
                            <div>
                                <label style={{fontSize:"12px",fontWeight:"600",color:"#e2e8f0",display:"block",marginBottom:"6px"}}>📅 Simülasyon Tarihi</label>
                                <MiniTakvim value={gecmisSimTarihi} onChange={setGecmisSimTarihi} />
                            </div>
                            <div>
                                <label style={{fontSize:"12px",fontWeight:"600",color:"#e2e8f0",display:"block",marginBottom:"6px"}}>💱 Referans Fiyat</label>
                                <div style={{display:"flex",gap:"8px"}}>
                                    {["Bugünkü","En Yüksek"].map(opt => (
                                        <button key={opt} type="button" onClick={() => setGecmisRefFiyat(opt)} style={{
                                            flex:1,padding:"10px",borderRadius:"8px",cursor:"pointer",fontSize:"13px",fontWeight:"500",
                                            border: gecmisRefFiyat===opt ? "2px solid #7c3aed" : "2px solid #334155",
                                            background: gecmisRefFiyat===opt ? "#7c3aed" : "#0f172a",
                                            color: gecmisRefFiyat===opt ? "white" : "#e2e8f0"}}>
                                            {opt==="Bugünkü" ? "📅 Bugünkü Fiyat" : "🏆 En Yüksek Fiyat"}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label style={{fontSize:"12px",fontWeight:"600",color:"#e2e8f0",display:"block",marginBottom:"6px"}}>🎯 Minimum Skor</label>
                                <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
                                    {[0,50,60,65,70,75,80].map(v => (
                                        <button key={v} type="button" onClick={() => setGecmisMinSkor(v)} style={{
                                            padding:"6px 12px",borderRadius:"8px",cursor:"pointer",fontSize:"13px",fontWeight:"500",
                                            border: gecmisMinSkor===v ? "2px solid #2563eb" : "2px solid #334155",
                                            background: gecmisMinSkor===v ? "#2563eb" : "#0f172a",
                                            color: gecmisMinSkor===v ? "white" : "#e2e8f0"}}>
                                            {v === 0 ? "Tümü" : `${v}+`}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div style={{display:"flex",gap:"8px",justifyContent:"flex-end"}}>
                            <button onClick={() => setGecmisPopupAdim(null)} style={{padding:"8px 18px",borderRadius:"8px",border:"1px solid #475569",background:"#1e293b",cursor:"pointer",fontSize:"13px",color:"#e2e8f0"}}>İptal</button>
                            <button onClick={() => { if (!gecmisSimTarihi) return; setGecmisPopupAdim("onay"); }} style={{padding:"8px 20px",borderRadius:"8px",border:"none",background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",color:"white",cursor:"pointer",fontSize:"13px",fontWeight:"600"}}>Devam Et →</button>
                        </div>
                    </div>
                </div>
            )}

            {gecmisPopupAdim === "onay" && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
                    <div style={{background:"#1e293b",borderRadius:"14px",padding:"2rem",width:"480px",maxWidth:"95vw",boxShadow:"0 12px 48px rgba(0,0,0,0.25)"}}>
                        <div style={{fontSize:"18px",fontWeight:"700",color:"#93c5fd",marginBottom:"0.75rem"}}>⚠️ Sayfadan Ayrılıyorsunuz</div>
                        <div style={{background:"#f59e0b22",border:"1px solid #92400e",borderRadius:"8px",padding:"1rem",
                            marginBottom:"1.25rem",fontSize:"13px",color:"#92400e",lineHeight:"1.7"}}>
                            Bu sayfa temizlenerek <strong>Geçmiş Simülasyon</strong> sayfasına yönlendirileceksiniz.
                            <br/><br/>Devam etmek istiyor musunuz?
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px",marginBottom:"1.25rem",fontSize:"12px"}}>
                            {[
                                ["Piyasa",            piyasa],
                                ["Para Birimi",       pb],
                                ["Vade",              vade],
                                ["Sembol Sayısı",     `${secilenler.size} sembol`],
                                ["Simülasyon Tarihi", (() => { const d = new Date(gecmisSimTarihi+"T00:00:00"); return d.toLocaleDateString("tr-TR",{day:"2-digit",month:"long",year:"numeric"}); })()],
                                ["Referans Fiyat",    gecmisRefFiyat],
                                ["Minimum Skor",      gecmisMinSkor === 0 ? "Tümü" : `${gecmisMinSkor}+`],
                            ].map(([k,v]) => (
                                <div key={k} style={{display:"flex",justifyContent:"space-between",background:"#182234",borderRadius:"6px",padding:"6px 10px"}}>
                                    <span style={{color:"#94a3b8"}}>{k}:</span>
                                    <strong style={{color:"#93c5fd"}}>{v}</strong>
                                </div>
                            ))}
                        </div>
                        <div style={{display:"flex",gap:"8px",justifyContent:"flex-end"}}>
                            <button onClick={() => setGecmisPopupAdim("kriter")} style={{padding:"8px 18px",borderRadius:"8px",border:"1px solid #475569",background:"#1e293b",cursor:"pointer",fontSize:"13px",color:"#e2e8f0"}}>← Geri</button>
                            <button onClick={() => setGecmisPopupAdim(null)} style={{padding:"8px 18px",borderRadius:"8px",border:"1px solid #475569",background:"#1e293b",cursor:"pointer",fontSize:"13px",color:"#e2e8f0"}}>Hayır, İptal</button>
                            <button onClick={gecmisSimOnay} style={{padding:"8px 20px",borderRadius:"8px",border:"none",background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",color:"white",cursor:"pointer",fontSize:"13px",fontWeight:"600"}}>Evet, Başlat 🚀</button>
                        </div>
                    </div>
                </div>
            )}



            {alarmPopup && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                    <div style={{ background: "#1e293b", borderRadius: "14px", padding: "1.5rem",
                        width: "420px", maxHeight: "80vh", overflowY: "auto",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
                        <h3 style={{ margin: "0 0 0.75rem", fontSize: "16px", color: "#93c5fd" }}>⏰ Alarm Ekle</h3>
                        <div style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "12px" }}>
                            Piyasa: <strong>{piyasa}</strong> · Para Birimi: <strong>{pb}</strong>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {alarmHisseler.map(h => {
                                const hedef = hedefFiyatlar[h.sembol] || "";
                                const fiyat = parseFloat(h.fiyat);
                                const hedefN = parseFloat(hedef);
                                const pct = hedef && !isNaN(hedefN) && fiyat > 0
                                    ? ((hedefN - fiyat) / fiyat * 100).toFixed(2) : null;
                                return (
                                    <div key={h.sembol} style={{ background: "#182234", borderRadius: "8px",
                                        padding: "10px 12px", border: "1px solid #334155" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "7px" }}>
                                            <strong style={{ fontSize: "14px" }}>{h.sembol}</strong>
                                            <span style={{ color: "#94a3b8", fontSize: "12px" }}>Güncel: {h.fiyat}</span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <span style={{ color: "#94a3b8", fontSize: "12px", minWidth: "80px" }}>Hedef Fiyat:</span>
                                            <input type="number" value={hedef}
                                                onChange={e => setHedefFiyatlar(prev => ({ ...prev, [h.sembol]: e.target.value }))}
                                                style={{ flex: 1, padding: "4px 8px", borderRadius: "6px",
                                                    border: "1px solid #475569", fontSize: "13px", textAlign: "right" }}
                                                placeholder="0.00" />
                                            {pct !== null && (
                                                <span style={{ fontSize: "12px", fontWeight: 600, minWidth: "52px",
                                                    color: parseFloat(pct) >= 0 ? "#16a34a" : "#dc2626" }}>
                                                    {parseFloat(pct) >= 0 ? "+" : ""}{pct}%
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ marginTop: "12px", fontSize: "12px", color: "#94a3b8" }}>Bildirim:</div>
                        <div style={{ display: "flex", gap: "16px", marginTop: "5px" }}>
                            {[["Mail", alarmMail, setAlarmMail], ["Pop-up", alarmBildirim, setAlarmBildirim]].map(([ad, val, setter]) => (
                                <label key={ad} style={{ display: "flex", alignItems: "center", gap: "5px",
                                    cursor: "pointer", fontSize: "13px" }}>
                                    <input type="checkbox" checked={val} onChange={e => setter(e.target.checked)} />
                                    {ad}
                                </label>
                            ))}
                        </div>
                        <div style={{ display: "flex", gap: "8px", marginTop: "1.25rem", justifyContent: "flex-end" }}>
                            <button onClick={() => { setAlarmPopup(false); setAlarmHisseler([]); }}
                                style={styles.btnVazgec}>Vazgeç</button>
                            <button onClick={alarmKaydet} disabled={alarmKaydediliyor}
                                style={{ ...styles.btnPrimary, background: alarmKaydediliyor ? "#94a3b8" : "#2563eb" }}>
                                {alarmKaydediliyor ? "Kaydediliyor..." : "Kaydet"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
    sayfa:        { padding: "1.5rem", background: "#0f172a", minHeight: "100vh", color: "#e2e8f0" },
    baslik:       { marginBottom: "0.25rem", fontSize: "20px", color: "#e2e8f0" },
    altBaslik:    { color: "#94a3b8", fontSize: "13px", marginBottom: "1.25rem" },
    filtreSatiri: { display: "flex", gap: "1rem", alignItems: "flex-end", flexWrap: "wrap",
                    background: "#1e293b", padding: "1.25rem", borderRadius: "12px",
                    marginBottom: "1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    filtreGrup:   { display: "flex", flexDirection: "column", gap: "4px" },
    etiket:       { fontSize: "12px", color: "#94a3b8", fontWeight: 500 },
    select:       { padding: "0.5rem 0.75rem", borderRadius: "8px", border: "1px solid #475569",
                    fontSize: "14px", minWidth: "140px", cursor: "pointer", background: "#0f172a", color: "#e2e8f0" },
    buton:        { padding: "0.55rem 1.25rem", color: "white", border: "none",
                    borderRadius: "8px", cursor: "pointer", fontSize: "14px", alignSelf: "flex-end" },
    bilgi:        { display: "flex", gap: "1rem", background: "#1e293b", padding: "0.75rem 1.25rem",
                    borderRadius: "8px", marginBottom: "1rem", fontSize: "13px",
                    flexWrap: "wrap", alignItems: "center", border: "1px solid #334155" },
    tablo:        { width: "100%", borderCollapse: "collapse", fontSize: "13px", background: "#1e293b",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)", borderRadius: "8px", overflow: "hidden" },
    thead:        { background: "#0f172a" },
    th:           { padding: "0.6rem 0.8rem", color: "white", textAlign: "left",
                    whiteSpace: "nowrap", fontSize: "12px" },
    td:           { padding: "0.5rem 0.8rem", borderBottom: "1px solid #334155", color: "#e2e8f0" },
    ilerlemeSarici: { background: "#1e293b", padding: "1rem 1.25rem", borderRadius: "12px",
                      marginBottom: "1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    ilerlemeUst:  { display: "flex", justifyContent: "space-between", marginBottom: "8px",
                    fontSize: "14px", fontWeight: 500 },
    ilerlemeArkaplan: { background: "#334155", borderRadius: "99px", height: "10px", overflow: "hidden" },
    ilerlemeBar:  { height: "100%", borderRadius: "99px", transition: "width 0.8s ease" },
    btnVazgec:    { padding: "8px 18px", borderRadius: "8px", border: "1px solid #475569",
                    background: "#1e293b", cursor: "pointer", fontSize: "13px", color: "#e2e8f0" },
    btnPrimary:   { padding: "8px 20px", borderRadius: "8px", border: "none",
                    background: "linear-gradient(135deg,#1d4ed8,#3b82f6)", color: "white", cursor: "pointer",
                    fontSize: "13px", fontWeight: 600 },
};
// ─── Mini Türkçe Takvim (Popup için) ──────────────────────────────────────────
const _TR_AYLAR = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran",
                   "Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];

function MiniTakvim({ value, onChange }) {
    const bugun = new Date(); bugun.setHours(0,0,0,0);
    const [gorunenYil, setGorunenYil] = useState(bugun.getFullYear());
    const [gorunenAy,  setGorunenAy]  = useState(bugun.getMonth());
    const [yilMod,     setYilMod]     = useState(false);
    const [ayMod,      setAyMod]      = useState(false);

    const seciliTarih = value ? (() => { const d = new Date(value + "T00:00:00"); d.setHours(0,0,0,0); return d; })() : null;
    const baslangicGun = (new Date(gorunenYil, gorunenAy, 1).getDay() + 6) % 7;
    const gunSayisi    = new Date(gorunenYil, gorunenAy + 1, 0).getDate();
    const gunler = [];
    for (let i = 0; i < baslangicGun; i++) gunler.push(null);
    for (let d = 1; d <= gunSayisi; d++) gunler.push(d);

    const gunSec = gun => {
        if (!gun) return;
        const t = new Date(gorunenYil, gorunenAy, gun); t.setHours(0,0,0,0);
        if (t >= bugun) return;
        onChange(`${gorunenYil}-${String(gorunenAy+1).padStart(2,"0")}-${String(gun).padStart(2,"0")}`);
    };
    const oncekiAy = () => {
        if (gorunenAy === 0) { setGorunenYil(y => y-1); setGorunenAy(11); }
        else setGorunenAy(m => m-1);
    };
    const sonrakiAy = () => {
        const ny = gorunenAy === 11 ? 0 : gorunenAy+1;
        const fy = gorunenAy === 11 ? gorunenYil+1 : gorunenYil;
        if (new Date(fy, ny, 1) >= bugun) return;
        setGorunenAy(ny); setGorunenYil(fy);
    };
    const yillar = [];
    for (let y = bugun.getFullYear(); y >= 1990; y--) yillar.push(y);
    const sonrakiDevre = new Date(gorunenAy === 11 ? gorunenYil+1 : gorunenYil, gorunenAy === 11 ? 0 : gorunenAy+1, 1) >= bugun;

    return (
        <div style={{border:"1px solid #334155", borderRadius:"10px", padding:"12px", background:"#0f172a", userSelect:"none"}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"8px"}}>
                <button type="button" onClick={oncekiAy} style={_navBtn}>&#8249;</button>
                <div style={{display:"flex", gap:"4px"}}>
                    <div style={{position:"relative"}}>
                        <button type="button" onClick={() => { setAyMod(v=>!v); setYilMod(false); }}
                            style={{fontWeight:"600", fontSize:"13px", color:"#93c5fd",
                                    background: ayMod ? "#3b82f622" : "none",
                                    border: ayMod ? "1px solid #3b82f655" : "1px solid transparent",
                                    cursor:"pointer", padding:"2px 7px", borderRadius:"5px"}}>
                            {_TR_AYLAR[gorunenAy]} &#9662;
                        </button>
                        {ayMod && (
                            <div style={{position:"absolute", top:"calc(100% + 3px)", left:"50%",
                                         transform:"translateX(-50%)", zIndex:700, background:"#1e293b",
                                         borderRadius:"8px", boxShadow:"0 4px 16px rgba(0,0,0,0.15)",
                                         border:"1px solid #334155", padding:"6px",
                                         display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"3px", minWidth:"175px"}}>
                                {_TR_AYLAR.map((ay, i) => {
                                    const dev = new Date(gorunenYil, i, 1) >= bugun;
                                    return (
                                        <button key={ay} type="button" disabled={dev}
                                            onClick={() => { setGorunenAy(i); setAyMod(false); }}
                                            style={{padding:"5px 3px", borderRadius:"5px", border:"none",
                                                    fontSize:"11px", cursor: dev ? "default" : "pointer",
                                                    background: i === gorunenAy ? "#2563eb" : "transparent",
                                                    color: i === gorunenAy ? "white" : dev ? "#475569" : "#e2e8f0",
                                                    fontWeight: i === gorunenAy ? "600" : "400"}}>
                                            {ay.slice(0,3)}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div style={{position:"relative"}}>
                        <button type="button" onClick={() => { setYilMod(v=>!v); setAyMod(false); }}
                            style={{fontWeight:"600", fontSize:"13px", color:"#93c5fd",
                                    background: yilMod ? "#3b82f622" : "none",
                                    border: yilMod ? "1px solid #3b82f655" : "1px solid transparent",
                                    cursor:"pointer", padding:"2px 7px", borderRadius:"5px"}}>
                            {gorunenYil} &#9662;
                        </button>
                        {yilMod && (
                            <div style={{position:"absolute", top:"calc(100% + 3px)", left:"50%",
                                         transform:"translateX(-50%)", zIndex:700, background:"#1e293b",
                                         borderRadius:"8px", boxShadow:"0 4px 16px rgba(0,0,0,0.15)",
                                         border:"1px solid #334155", maxHeight:"160px", overflowY:"auto", minWidth:"75px"}}>
                                {yillar.map(y => (
                                    <button key={y} type="button" onClick={() => { setGorunenYil(y); setYilMod(false); }}
                                        style={{display:"block", width:"100%", padding:"5px 12px", border:"none",
                                                textAlign:"center", fontSize:"12px", cursor:"pointer",
                                                background: y === gorunenYil ? "#2563eb" : "transparent",
                                                color: y === gorunenYil ? "white" : "#e2e8f0",
                                                fontWeight: y === gorunenYil ? "600" : "400"}}>
                                        {y}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <button type="button" onClick={sonrakiAy}
                    style={{..._navBtn, opacity: sonrakiDevre ? 0.3 : 1, cursor: sonrakiDevre ? "default" : "pointer"}}>&#8250;</button>
            </div>
            <div style={{display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:"1px", marginBottom:"3px"}}>
                {["Pt","Sa","Ça","Pe","Cu","Ct","Pz"].map(g => (
                    <div key={g} style={{textAlign:"center", fontSize:"10px", color:"#94a3b8", fontWeight:"600", padding:"2px"}}>{g}</div>
                ))}
            </div>
            <div style={{display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:"1px"}}>
                {gunler.map((gun, i) => {
                    if (!gun) return <div key={"e"+i} />;
                    const t = new Date(gorunenYil, gorunenAy, gun); t.setHours(0,0,0,0);
                    const gelecek = t >= bugun;
                    const secili  = seciliTarih && t.getTime() === seciliTarih.getTime();
                    const bugunku = t.getTime() === bugun.getTime();
                    return (
                        <button key={gun} type="button" onClick={() => gunSec(gun)} disabled={gelecek}
                            style={{padding:"4px 2px", border:"none", borderRadius:"5px",
                                    fontSize:"11px", cursor: gelecek ? "default" : "pointer",
                                    background: secili ? "#2563eb" : bugunku ? "#3b82f622" : "transparent",
                                    color: secili ? "white" : gelecek ? "#475569" : bugunku ? "#2563eb" : "#e2e8f0",
                                    fontWeight: secili || bugunku ? "700" : "400"}}>
                            {gun}
                        </button>
                    );
                })}
            </div>
            <div style={{borderTop:"1px solid #334155", marginTop:"8px", paddingTop:"7px",
                         display:"flex", gap:"4px", flexWrap:"wrap"}}>
                {[["1 Ay",1],["3 Ay",3],["6 Ay",6],["1 Yıl",12],["2 Yıl",24],["3 Yıl",36]].map(([label, ay]) => (
                    <button key={label} type="button" onClick={() => {
                        const d = new Date(bugun); d.setMonth(d.getMonth() - ay);
                        const iso = d.toISOString().split("T")[0];
                        onChange(iso); setGorunenYil(d.getFullYear()); setGorunenAy(d.getMonth());
                    }} style={{padding:"2px 8px", borderRadius:"10px", fontSize:"10px",
                               border:"1px solid #334155", background:"#1e293b", cursor:"pointer", color:"#e2e8f0"}}>
                        {label}
                    </button>
                ))}
            </div>
            {value && (
                <div style={{textAlign:"center", marginTop:"8px", paddingTop:"7px",
                             borderTop:"1px solid #334155", fontSize:"12px", color:"#93c5fd", fontWeight:"600"}}>
                    {(() => { const d = new Date(value + "T00:00:00");
                        return String(d.getDate()).padStart(2,"0") + " " + _TR_AYLAR[d.getMonth()] + " " + d.getFullYear(); })()}
                </div>
            )}
        </div>
    );
}
const _navBtn = {padding:"3px 9px", border:"1px solid #334155", borderRadius:"5px",
                 background:"#1e293b", cursor:"pointer", fontSize:"15px", color:"#e2e8f0", lineHeight:"1"};


